// @ts-nocheck
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
// FIX: Use relative path to resolve types module.
import { View, Child, ChildLearningPlan, PlanStatus, LearningHistory, PlanDifficulty, PlanDuration, Assignment, Homework, Submission, LearningModule, UserSettings, Notification, Class, Student } from '../types';
import { generateChildLearningPlan, generateImage, formatAssetUrl, uploadImageResource } from '../services/apiService';
import Card from './common/Card';
import Spinner from './common/Spinner';
import { ArrowLeftIcon, ArrowPathIcon, TrophyIcon, SparklesIcon, XMarkIcon, TrashIcon, CheckIcon, PlayIcon, CheckBadgeIcon, ClockIcon, FlagIcon, ChevronDownIcon, UploadIcon, ClipboardDocumentListIcon, PencilIcon, PhotoIcon, ZoomInIcon, ArrowDownTrayIcon, PlusIcon, AcademicCapIcon, ChatBubbleBottomCenterTextIcon, HistoryIcon, ChartBarIcon, UserCircleIcon, BellIcon, CalendarIcon } from './common/Icons';
import { CreditCost } from '../config';
import { getCreditCostSuffix } from '../utils/creditUi';
import { getDashboardGreeting } from '../utils/dashboardGreetings';
import { getStatusDisplay, getTypeColorConfig, isSubmissionGraded, isSubmissionSubmitted, getLastSubmission, SUBMISSION_STATUS, formatGrade, parseSubmissionContent, normalizeAnswersForTasks } from '../utils/assignmentUtils';

import { getUserLimits, checkLimit } from '../utils/limitUtils';

interface ChildDashboardProps {
  onBack: () => void;
  onNavigate: (view: View, payload?: object) => void;
  userSettings: UserSettings;
  child: Child;
  isWorkingMode?: boolean;
  focusSection?: 'progress' | 'homework';
  learningPlans: ChildLearningPlan[];
  learningHistory: LearningHistory[];
  assignments: Assignment[];
  homeworks: Homework[];
  submissions: Submission[];
  classes: Class[];
  students: Student[];
  allChildren?: Child[];
  notifications?: Notification[];
  joinRequests?: any[];
  onSavePlan: (plan: Omit<ChildLearningPlan, 'id'>) => Promise<ChildLearningPlan>;
  onUpdatePlan: (plan: ChildLearningPlan) => void;
  onDeletePlan: (id: string) => void;
  onStudentSubmit: (assignmentId: string, studentId: string, results?: any) => void;
  onCreditUsage: (cost: number, actionName: string) => boolean;
  onUpdateSettings?: (update: React.SetStateAction<UserSettings | null>) => void;
  onRefreshData?: () => void;
  onAddNotification?: (notif: Partial<Notification>, showToast?: boolean) => void;
  onNotificationRead?: (id: string) => void;
  isLoading?: boolean;
  resourceLoadingStates?: Record<string, boolean>;
}

import HomeworkViewer from './common/HomeworkViewer';
import { ChildDashboardSkeleton } from './common/Skeletons';

function formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

const LearningPlanModal: React.FC<{
    child: Child;
    student?: Student;
    onClose: () => void;
    onSave: (plan: Omit<ChildLearningPlan, 'id'>) => void;
    onCreditUsage: (cost: number, actionName: string) => boolean;
}> = ({ child, student, onClose, onSave, onCreditUsage }) => {
    const [goal, setGoal] = useState('');
    const [difficulty, setDifficulty] = useState<PlanDifficulty>(PlanDifficulty.Medium);
    const [duration, setDuration] = useState<PlanDuration>(PlanDuration.Short);
    const [isLoading, setIsLoading] = useState(false);
    const mainContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isLoading && mainContainerRef.current) {
            mainContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isLoading]);
    const [error, setError] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [generatedPlan, setGeneratedPlan] = useState<Pick<ChildLearningPlan, 'title' | 'dailyPlans'> | null>(null);

    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const MAX_FILES = 10;
    const MAX_FILE_SIZE_MB = 2;


    const handleSampleData = () => {
        let sampleGoal = '';
        if (child.age < 6) {
            sampleGoal = `Giúp con ${child.name} làm quen với các chữ cái và số đếm từ 1 đến 10 qua các trò chơi vui nhộn.`;
        } else if (child.age >= 6 && child.age < 10) {
            sampleGoal = `Cùng con ${child.name} rèn luyện kỹ năng đọc hiểu truyện ngắn và luyện tập các phép toán cộng trừ trong phạm vi 100.`;
        } else {
            sampleGoal = `Giúp ${child.name} cải thiện kỹ năng viết đoạn văn ngắn (3-5 câu) và học 20 từ vựng tiếng Anh mới về chủ đề thiên nhiên.`;
        }
        setGoal(sampleGoal);
        setError('');
        setGeneratedPlan(null);
    };

    const addFiles = (newFiles: File[]) => {
        setError(''); 
        let validFiles: File[] = [];
        if (files.length + newFiles.length > MAX_FILES) {
            setError(`Bạn chỉ có thể tải lên tối đa ${MAX_FILES} tệp.`);
            return;
        }

        for (const file of newFiles) {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`Tệp "${file.name}" quá lớn (tối đa ${MAX_FILE_SIZE_MB}MB).`);
                return;
            }
            validFiles.push(file);
        }
        setFiles(prev => [...prev, ...validFiles]);
    };
    
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            addFiles(Array.from(e.target.files));
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files) {
            addFiles(Array.from(e.dataTransfer.files));
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleGenerate = async () => {
        if (!goal.trim()) {
            setError('Vui lòng nhập mục tiêu học tập.');
            return;
        }
        if (!onCreditUsage(CreditCost.CHILD_PLAN ?? CreditCost.ACTIVITY, `Tạo lộ trình học tập cho con ${child.name}`)) {
            setError('Không đủ credit để thực hiện. Vui lòng nạp thêm.');
            return;
        }
        setError('');
        setIsLoading(true);
        setGeneratedPlan(null);
        try {
            const fileReaders = files
                .filter(file => file.type === 'text/plain')
                .map(file => new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(`--- BẮT ĐẦU TỆP: ${file.name} ---\n${e.target?.result}\n--- KẾT THÚC TỆP ---\n\n`);
                    reader.onerror = () => reject(new Error(`Lỗi đọc tệp ${file.name}`));
                    reader.readAsText(file);
                }));

            const fileContents = await Promise.all(fileReaders);
            const fileContext = fileContents.length > 0 ? fileContents.join('') : null;
            
            const plan = await generateChildLearningPlan(child.age, child.gender, goal, difficulty, duration, fileContext);
            
            // Tìm các tài liệu liên quan đến học sinh (student) nếu có để cung cấp context cho AI
            // (Thực hiện bởi hàm generateChildLearningPlan phía trên nếu backend hỗ trợ)
            
            // Làm sạch placeholder images ngay khi nhận kết quả từ AI
            if (plan && plan.dailyPlans) {
                plan.dailyPlans = plan.dailyPlans.map(day => ({
                    ...day,
                    modules: day.modules.map(mod => {
                        let cleanImageUrl = mod.imageUrl;
                        let cleanImagePrompt = mod.imagePrompt || '';
                        
                        if (cleanImageUrl && (cleanImageUrl.startsWith('url_to_image') || cleanImageUrl.includes('placeholder'))) {
                            if (!cleanImagePrompt) {
                                cleanImagePrompt = cleanImageUrl.replace(/_/g, ' ').replace('url to image of ', '');
                            }
                            cleanImageUrl = '';
                        }
                        
                        return { ...mod, imageUrl: cleanImageUrl, imagePrompt: cleanImagePrompt };
                    })
                }));
            }
            
            setGeneratedPlan(plan);
        } catch (err) {
            setError((err as Error).message || 'Không thể tạo kế hoạch. Vui lòng thử lại.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSavePlan = async () => {
        if (generatedPlan) {
            // Đảm bảo tất cả module đều có ID và làm sạch placeholder image URL
            const planWithIds = {
                ...generatedPlan,
                dailyPlans: generatedPlan.dailyPlans.map(day => ({
                    ...day,
                    modules: day.modules.map(mod => {
                        let cleanImageUrl = mod.imageUrl;
                        let cleanImagePrompt = mod.imagePrompt || '';
                        
                        // Nếu imageUrl là placeholder (ví dụ: url_to_image_...)
                        if (cleanImageUrl && (cleanImageUrl.startsWith('url_to_image') || cleanImageUrl.includes('placeholder'))) {
                            if (!cleanImagePrompt) {
                                // Sử dụng slug làm prompt
                                cleanImagePrompt = cleanImageUrl.replace(/_/g, ' ').replace('url to image of ', '');
                            }
                            cleanImageUrl = ''; // Xóa placeholder
                        }

                        return {
                            ...mod,
                            id: mod.id || `mod-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            imageUrl: cleanImageUrl,
                            imagePrompt: cleanImagePrompt
                        };
                    })
                }))
            };

            await onSave({
                childId: child.id,
                goal,
                difficulty,
                duration,
                startDate,
                createdAt: new Date().toISOString(),
                planStatus: PlanStatus.OnTrack,
                ...planWithIds,
            });
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={onClose}>
            <div ref={mainContainerRef} className="bg-white rounded-xl shadow-2xl w-full max-w-3xl m-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b flex justify-between items-center">
                    <h3 className="text-xl font-bold text-gray-900">Tạo Lộ trình Học tập cho {child.name}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><XMarkIcon className="h-5 w-5 text-gray-600" /></button>
                </div>
                
                <div className="p-6 flex-grow overflow-y-auto space-y-6">
                    <div>
                        <div className="flex justify-between items-center">
                             <label htmlFor="goal" className="block text-sm font-semibold text-gray-700 mb-1">1. Đặt mục tiêu cho con</label>
                             <button onClick={handleSampleData} className="text-xs font-medium text-teal-600 hover:text-teal-800">Sử dụng dữ liệu mẫu</button>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">Mục tiêu càng cụ thể, lộ trình học AI tạo ra càng phù hợp.</p>
                        <textarea id="goal" value={goal} onChange={e => setGoal(e.target.value)} placeholder="Ví dụ: Giúp con học thuộc bảng cửu chương 2 và 3, hoặc Cải thiện kỹ năng đọc hiểu tiếng Anh qua truyện tranh..." rows={3} className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">2. Cung cấp tài liệu tham khảo (Tùy chọn)</label>
                        <p className="text-xs text-gray-500 mb-2">Tải lên tài liệu (.txt, .pdf, .doc) để AI có thêm ngữ cảnh cho việc tạo lộ trình.</p>
                        
                        <div 
                            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onDrop={handleDrop}
                            className={`relative flex flex-col items-center justify-center w-full border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer hover:border-gray-400 ${isDragging ? 'border-teal-500 bg-teal-50' : 'border-gray-300'}`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadIcon className="mx-auto h-8 w-8 text-gray-400" />
                            <p className="mt-2 block text-sm font-medium text-gray-900">Kéo thả hoặc <span className="text-teal-600">chọn tệp</span></p>
                            <p className="mt-1 text-xs text-gray-500">Hỗ trợ .txt, .pdf, .doc, .docx. Tối đa ${MAX_FILES} tệp, mỗi tệp &lt; ${MAX_FILE_SIZE_MB}MB.</p>
                            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} accept=".txt,.pdf,.doc,.docx" />
                        </div>
                        
                        {files.length > 0 && (
                            <div className="mt-3 space-y-2 max-h-24 overflow-y-auto pr-2">
                                {files.map((file, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-gray-100 rounded-md text-sm">
                                        <div className="flex items-center gap-2 overflow-hidden">
                                            <span className="font-medium truncate">{file.name}</span>
                                            <span className="text-gray-500 flex-shrink-0">{formatBytes(file.size)}</span>
                                        </div>
                                        <button onClick={() => removeFile(index)} className="p-1 text-gray-500 hover:text-red-600 rounded-full"><XMarkIcon className="h-4 w-4"/></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {error && <p className="text-sm text-red-500 mt-1">{error}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">3. Chọn mức độ khó</label>
                            <div className="space-y-2">
                                {/* FIX: Add explicit type to map callback parameter to resolve 'unknown' type error. */}
                                {Object.values(PlanDifficulty).map((level: PlanDifficulty) => (
                                    <label key={level} className={`flex items-center p-3 border rounded-lg cursor-pointer ${difficulty === level ? 'bg-teal-50 border-teal-400' : 'bg-white'}`}>
                                        <input type="radio" name="difficulty" value={level} checked={difficulty === level} onChange={() => setDifficulty(level)} className="h-4 w-4 text-teal-600 focus:ring-teal-500"/>
                                        <span className="ml-3 text-sm font-medium text-gray-800">{level}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                         <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">4. Chọn thời lượng</label>
                            <div className="space-y-2">
                                {/* FIX: Add explicit type to map callback parameter to resolve 'unknown' type error. */}
                                {Object.values(PlanDuration).map((d: PlanDuration) => (
                                    <label key={d} className={`flex items-center p-3 border rounded-lg cursor-pointer ${duration === d ? 'bg-teal-50 border-teal-400' : 'bg-white'}`}>
                                        <input type="radio" name="duration" value={d} checked={duration === d} onChange={() => setDuration(d)} className="h-4 w-4 text-teal-600 focus:ring-teal-500"/>
                                        <span className="ml-3 text-sm font-medium text-gray-800">{d}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div>
                        <label htmlFor="startDate" className="block text-sm font-semibold text-gray-700 mb-1">5. Thời gian bắt đầu học</label>
                        <p className="text-xs text-gray-500 mb-2">Lộ trình sẽ được kích hoạt từ ngày này.</p>
                        <input 
                            id="startDate" 
                            type="date" 
                            value={startDate} 
                            onChange={e => setStartDate(e.target.value)} 
                            className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-500" 
                        />
                    </div>

                    <div className="text-center">
                        <button onClick={handleGenerate} disabled={isLoading || !goal.trim()} className="flex items-center justify-center gap-2 px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 disabled:bg-gray-400 transition-colors">
                            {isLoading ? <Spinner size="sm"/> : <SparklesIcon className="h-5 w-5" />}
                            <span>{isLoading ? 'AI đang thiết kế...' : `6. Nhờ AI thiết kế lộ trình${getCreditCostSuffix(CreditCost.LESSON_PLAN)}`}</span>
                        </button>
                    </div>

                    {generatedPlan && (
                        <div className="p-4 border-2 border-dashed border-green-300 bg-green-50 rounded-lg">
                            <h4 className="text-lg font-bold text-green-800">Lộ trình được đề xuất: "{generatedPlan.title}"</h4>
                             <div className="mt-4 space-y-3 text-sm text-gray-700">
                                {generatedPlan.dailyPlans.map((day, dayIndex) => (
                                    <div key={dayIndex}>
                                        <p className="font-semibold">{day.day}:</p>
                                        <ul className="list-disc list-inside pl-4">
                                            {day.modules.map((module, moduleIndex) => <li key={moduleIndex}>{module.title}</li>)}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl">
                    <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Hủy</button>
                    <button onClick={handleSavePlan} disabled={!generatedPlan} className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:bg-gray-400 flex items-center gap-2">
                        <CheckIcon className="h-4 w-4" />
                        <span>Lưu Lộ trình</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

const HistoryModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    history: LearningHistory[];
    plans: ChildLearningPlan[];
}> = ({ isOpen, onClose, history, plans }) => {
    if (!isOpen) return null;

    const getModuleInfo = (moduleId: string) => {
        for (const plan of plans) {
            for (const day of plan.dailyPlans) {
                const module = day.modules.find(m => m.id === moduleId);
                if (module) return { moduleTitle: module.title, planTitle: plan.title };
            }
        }
        return { moduleTitle: "Module không xác định", planTitle: "" };
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl m-4 flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b"><h3 className="text-xl font-bold">Lịch sử học tập</h3></div>
                <div className="p-6 flex-grow overflow-y-auto">
                    {history.length === 0 ? (
                        <p>Chưa có hoạt động nào được ghi nhận.</p>
                    ) : (
                        <ul className="space-y-3">
                            {history.slice().reverse().map(entry => {
                                const { moduleTitle, planTitle } = getModuleInfo(entry.moduleId);
                                return (
                                    <li key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                        <div>
                                            <p className="font-semibold text-gray-800">{moduleTitle}</p>
                                            {planTitle && <p className="text-xs text-teal-600 font-medium italic">Lộ trình: {planTitle}</p>}
                                            <p className="text-xs font-semibold text-slate-500">{new Date(entry.completedAt).toLocaleString('vi-VN')}</p>
                                        </div>
                                        <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${entry.isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {entry.isCorrect ? 'Đúng' : 'Sai'}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end rounded-b-xl">
                    <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Đóng</button>
                </div>
            </div>
        </div>
    );
};


const FullScreenImageModal: React.FC<{
    url: string;
    onClose: () => void;
}> = ({ url, onClose }) => {
    if (!url) return null;
    return (
        <div 
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-90" 
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <button 
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }} 
                className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-full"
            >
                <XMarkIcon className="h-8 w-8" />
            </button>
            <img 
                src={formatAssetUrl(url)} 
                alt="Full screen preview" 
                className="max-w-full max-h-full object-contain" 
                onClick={e => e.stopPropagation()} 
            />
        </div>
    );
};

const ModuleEditModal: React.FC<{
    module: LearningModule;
    onClose: () => void;
    onSave: (updatedModule: LearningModule) => void;
    onCreditUsage: (cost: number, actionName: string) => boolean;
}> = (props) => {
    const { module, onClose, onSave, onCreditUsage } = props;
    const [title, setTitle] = useState(module.title);
    const [content, setContent] = useState(module.content);
    const [imagePrompt, setImagePrompt] = useState(module.imagePrompt || '');
    const [imageUrl, setImageUrl] = useState(module.imageUrl || '');
    const [questionText, setQuestionText] = useState(module.question.text);
    const [options, setOptions] = useState<string[]>([...module.question.options]);
    const [optionImages, setOptionImages] = useState<string[]>(module.question.optionImages || []);
    const [correctAnswer, setCorrectAnswer] = useState(module.question.correctAnswer);
    const [questionImage, setQuestionImage] = useState(module.question.image || '');
    
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingQuestionImage, setIsGeneratingQuestionImage] = useState(false);
    // Track processing state for each option index
    const [processingOptions, setProcessingOptions] = useState<Record<number, boolean>>({});
    const [isUploading, setIsUploading] = useState(false);
    const [isUploadingQuestionImage, setIsUploadingQuestionImage] = useState(false);
    const [fullScreenUrl, setFullScreenUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const questionFileInputRef = useRef<HTMLInputElement>(null);
    const optionFileInputRef = useRef<HTMLInputElement>(null);
    const [uploadingOptionIndex, setUploadingOptionIndex] = useState<number | null>(null);

    const handleClose = () => {
        // Kiểm tra xem có thay đổi so với dữ liệu ban đầu không
        const hasChanged = 
            title !== module.title ||
            content !== module.content ||
            imagePrompt !== (module.imagePrompt || '') ||
            imageUrl !== (module.imageUrl || '') ||
            questionText !== module.question.text ||
            questionImage !== (module.question.image || '') ||
            JSON.stringify(options) !== JSON.stringify(module.question.options) ||
            JSON.stringify(optionImages) !== JSON.stringify(module.question.optionImages || []) ||
            correctAnswer !== module.question.correctAnswer;

        if (hasChanged) {
            if (window.confirm('Bạn có các thay đổi chưa được lưu. Bạn có chắc chắn muốn đóng và hủy bỏ các thay đổi này không?')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const handleGenerateImage = async () => {
        if (!imagePrompt.trim()) return;
        
        if (!onCreditUsage(CreditCost.IMAGE, `Tạo ảnh minh họa cho module: ${title}`)) {
            alert('Không đủ credit để tạo ảnh. Vui lòng nạp thêm.');
            return;
        }

        setIsGenerating(true);
        try {
            const generated = await generateImage(imagePrompt);
            setImageUrl(generated.imageUrl);
        } catch (error) {
            alert('Lỗi khi tạo ảnh: ' + (error as Error).message);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleGenerateQuestionImage = async () => {
        if (!questionText.trim()) {
            alert('Vui lòng nhập nội dung câu hỏi trước khi tạo ảnh.');
            return;
        }
        
        if (!onCreditUsage(CreditCost.IMAGE, `Tạo ảnh minh họa cho câu hỏi: ${questionText}`)) {
            alert('Không đủ credit để tạo ảnh. Vui lòng nạp thêm.');
            return;
        }

        setIsGeneratingQuestionImage(true);
        try {
            const generated = await generateImage(`minh họa cho câu hỏi: ${questionText}`);
            setQuestionImage(generated.imageUrl);
        } catch (error) {
            alert('Lỗi khi tạo ảnh: ' + (error as Error).message);
        } finally {
            setIsGeneratingQuestionImage(false);
        }
    };

    const handleGenerateOptionImage = async (index: number) => {
        const optionText = options[index];
        if (!optionText.trim()) {
            alert('Vui lòng nhập nội dung cho đáp án này trước khi tạo ảnh.');
            return;
        }
        
        if (!onCreditUsage(CreditCost.IMAGE, `Tạo ảnh minh họa cho đáp án: ${optionText}`)) {
            alert('Không đủ credit để tạo ảnh. Vui lòng nạp thêm.');
            return;
        }

        setProcessingOptions(prev => ({ ...prev, [index]: true }));
        try {
            const generated = await generateImage(`minh họa cho: ${optionText}`);
            setOptionImages(prev => {
                const newImages = [...prev];
                while (newImages.length <= index) newImages.push('');
                newImages[index] = generated.imageUrl;
                return newImages;
            });
        } catch (error) {
            alert('Lỗi khi tạo ảnh: ' + (error as Error).message);
        } finally {
            setProcessingOptions(prev => {
                const newState = { ...prev };
                delete newState[index];
                return newState;
            });
        }
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const url = await uploadImageResource(file);
            setImageUrl(url);
        } catch (error) {
            alert('Lỗi khi tải ảnh lên: ' + (error as Error).message);
        } finally {
            setIsUploading(false);
        }
    };

    const handleQuestionFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsUploadingQuestionImage(true);
        try {
            const url = await uploadImageResource(file);
            setQuestionImage(url);
        } catch (error) {
            alert('Lỗi khi tải ảnh lên: ' + (error as Error).message);
        } finally {
            setIsUploadingQuestionImage(false);
        }
    };

    const handleOptionFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setProcessingOptions(prev => ({ ...prev, [index]: true }));
        try {
            const url = await uploadImageResource(file);
            const newOptionImages = [...optionImages];
            while (newOptionImages.length <= index) newOptionImages.push('');
            newOptionImages[index] = url;
            setOptionImages(newOptionImages);
        } catch (error) {
            alert('Lỗi khi tải ảnh lên: ' + (error as Error).message);
        } finally {
            setProcessingOptions(prev => {
                const newState = { ...prev };
                delete newState[index];
                return newState;
            });
        }
    };

    const handleDownload = (url: string) => {
        if (!url) return;
        const link = document.createElement('a');
        link.href = formatAssetUrl(url);
        link.target = "_blank";
        link.download = `image-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleAddOption = () => {
        setOptions([...options, '']);
        setOptionImages([...optionImages, '']);
    };

    const handleRemoveOption = (index: number) => {
        const newOptions = options.filter((_, i) => i !== index);
        const newOptionImages = optionImages.filter((_, i) => i !== index);
        setOptions(newOptions);
        setOptionImages(newOptionImages);
        if (correctAnswer === options[index]) {
            setCorrectAnswer(newOptions[0] || '');
        }
    };

    const handleSave = () => {
        const filteredOptions: string[] = [];
        const filteredOptionImages: string[] = [];
        
        options.forEach((opt, idx) => {
            if (opt.trim() !== '') {
                filteredOptions.push(opt);
                filteredOptionImages.push(optionImages[idx] || '');
            }
        });

        const { text, options: oldOptions, question: oldQuestion, ...restModule } = module as any;

        onSave({
            ...restModule,
            title,
            content,
            imagePrompt,
            imageUrl,
            question: {
                text: questionText,
                image: questionImage,
                options: filteredOptions,
                optionImages: filteredOptionImages,
                correctAnswer
            }
        });
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-60" onClick={handleClose}>
            {fullScreenUrl && <FullScreenImageModal url={fullScreenUrl} onClose={() => setFullScreenUrl(null)} />}
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl m-4 flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-900">Chỉnh sửa Module học tập</h3>
                    <button onClick={handleClose} className="p-1 rounded-full hover:bg-gray-200"><XMarkIcon className="h-5 w-5 text-gray-600" /></button>
                </div>
                
                <div className="p-6 overflow-y-auto space-y-6">
                    <section className="space-y-4">
                        <h4 className="font-bold text-teal-700 border-b pb-1">1. Nội dung bài học</h4>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Tiêu đề bài học</label>
                            <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm" />
                        </div>
                        
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Nội dung bài học</label>
                            <textarea value={content} onChange={e => setContent(e.target.value)} rows={4} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm" />
                        </div>

                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Hình ảnh minh họa</label>
                            <div className="mt-2 flex flex-col md:flex-row gap-4 items-start">
                                <div className="relative group w-full md:w-48 aspect-video rounded-lg overflow-hidden border bg-gray-50 flex items-center justify-center">
                                    {(isUploading || isGenerating) ? (
                                        <div className="flex flex-col items-center gap-2">
                                            <Spinner size="md" />
                                            <span className="text-xs text-teal-600 font-medium">{isUploading ? 'Đang tải lên...' : 'Đang tạo ảnh...'}</span>
                                        </div>
                                    ) : imageUrl ? (
                                        <>
                                            <img src={formatAssetUrl(imageUrl)} alt="Module Illustration" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                <button onClick={() => setFullScreenUrl(imageUrl)} className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-md" title="Phóng to">
                                                    <ZoomInIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => handleDownload(imageUrl)} className="p-1.5 bg-white/20 hover:bg-white/40 text-white rounded-md" title="Tải xuống">
                                                    <ArrowDownTrayIcon className="h-4 w-4" />
                                                </button>
                                                <button onClick={() => setImageUrl('')} className="p-1.5 bg-red-500/80 hover:bg-red-500 text-white rounded-md" title="Xóa">
                                                    <TrashIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-gray-400 flex flex-col items-center">
                                            <PhotoIcon className="h-8 w-8 mb-1" />
                                            <span className="text-xs">Chưa có ảnh</span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex-grow space-y-3 w-full">
                                    <div className="flex gap-2">
                                        <input 
                                            type="text" 
                                            value={imagePrompt} 
                                            onChange={e => setImagePrompt(e.target.value)} 
                                            placeholder="Mô tả hình ảnh..." 
                                            className="flex-grow p-2 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
                                        />
                                        {Boolean(imagePrompt.trim()) && (
                                            <button 
                                                onClick={handleGenerateImage}
                                                disabled={isGenerating}
                                                className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-xs font-bold rounded-md hover:bg-purple-700 disabled:bg-gray-400 whitespace-nowrap"
                                            >
                                                {isGenerating ? <Spinner size="sm"/> : <SparklesIcon className="h-3.5 w-3.5"/>}
                                                Tạo ảnh AI
                                            </button>
                                        )}
                                    </div>
                                    <input ref={fileInputRef} type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
                                    <button 
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {isUploading ? <Spinner size="sm" /> : <UploadIcon className="h-3.5 w-3.5" />}
                                        {isUploading ? 'Đang tải...' : 'Tải ảnh lên'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="space-y-4">
                        <h4 className="font-bold text-teal-700 border-b pb-1">2. Câu hỏi kiểm tra</h4>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">Nội dung câu hỏi</label>
                                <input type="text" value={questionText} onChange={e => setQuestionText(e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm" />
                            </div>

                            <div className="flex flex-col md:flex-row gap-4 items-start bg-slate-50 p-3 rounded-lg border border-slate-200">
                                <div className="relative group w-full md:w-32 aspect-square rounded-lg overflow-hidden border bg-white flex items-center justify-center flex-shrink-0">
                                    {(isUploadingQuestionImage || isGeneratingQuestionImage) ? (
                                        <div className="flex flex-col items-center gap-1">
                                            <Spinner size="sm" />
                                            <span className="text-xs text-teal-600 font-medium">{isUploadingQuestionImage ? 'Đang tải...' : 'Đang tạo...'}</span>
                                        </div>
                                    ) : questionImage ? (
                                        <>
                                            <img src={formatAssetUrl(questionImage)} alt="Question Illustration" className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                                <button onClick={() => setFullScreenUrl(questionImage)} className="p-1 bg-white/20 hover:bg-white/40 text-white rounded-md" title="Phóng to">
                                                    <ZoomInIcon className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => handleDownload(questionImage)} className="p-1 bg-white/20 hover:bg-white/40 text-white rounded-md" title="Tải xuống">
                                                    <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                                                </button>
                                                <button onClick={() => setQuestionImage('')} className="p-1 bg-red-500/80 hover:bg-red-500 text-white rounded-md" title="Xóa">
                                                    <TrashIcon className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-gray-300 flex flex-col items-center">
                                            <PhotoIcon className="h-6 w-6 mb-1" />
                                            <span className="text-xs">Ảnh câu hỏi</span>
                                        </div>
                                    )}
                                </div>
                                
                                <div className="flex flex-col gap-2 w-full">
                                    <p className="text-xs font-semibold text-slate-500 font-medium">Hình ảnh cho câu hỏi (tùy chọn)</p>
                                    <div className="flex gap-2">
                                        <button 
                                            onClick={() => questionFileInputRef.current?.click()}
                                            disabled={isUploadingQuestionImage}
                                            className="flex-grow flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                        >
                                            <UploadIcon className="h-3.5 w-3.5" />
                                            Tải ảnh
                                        </button>
                                        {Boolean(questionText.trim()) && (
                                            <button 
                                                onClick={handleGenerateQuestionImage}
                                                disabled={isGeneratingQuestionImage}
                                                className="flex-grow flex items-center justify-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-md hover:bg-purple-200 disabled:bg-gray-200 disabled:text-gray-400"
                                            >
                                                <SparklesIcon className="h-3.5 w-3.5" />
                                                Tạo ảnh AI
                                            </button>
                                        )}
                                    </div>
                                    <input ref={questionFileInputRef} type="file" className="hidden" accept="image/*" onChange={handleQuestionFileUpload} />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3 mt-4">
                            <label className="block text-xs font-semibold text-gray-500">Các lựa chọn trả lời </label>
                            {options.map((option, idx) => (
                                <div key={idx} className="flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex items-center gap-2">
                                        <input 
                                            type="radio" 
                                            name="correctAnswer" 
                                            checked={correctAnswer === option} 
                                            onChange={() => setCorrectAnswer(option)}
                                            className="h-4 w-4 text-teal-600"
                                        />
                                        <input 
                                            type="text" 
                                            value={option} 
                                            onChange={e => {
                                                const newOptions = [...options];
                                                newOptions[idx] = e.target.value;
                                                if (correctAnswer === option) setCorrectAnswer(e.target.value);
                                                setOptions(newOptions);
                                            }}
                                            placeholder={`Lựa chọn ${idx + 1}`}
                                            className="flex-grow p-1.5 text-sm bg-transparent border-b border-gray-300 focus:border-teal-500 outline-none"
                                        />
                                        <button onClick={() => handleRemoveOption(idx)} className="text-gray-400 hover:text-red-500">
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                    
                                    <div className="flex items-center gap-3 ml-6">
                                        <div className="relative group w-12 h-12 rounded border bg-white overflow-hidden flex items-center justify-center flex-shrink-0">
                                            {processingOptions[idx] ? (
                                                <Spinner size="sm" />
                                            ) : optionImages[idx] ? (
                                                <>
                                                    <img src={formatAssetUrl(optionImages[idx])} className="w-full h-full object-cover" alt={`Option ${idx + 1}`} />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                                        <button onClick={() => setFullScreenUrl(optionImages[idx])} className="p-1 bg-white/20 hover:bg-white/40 text-white rounded-md" title="Phóng to">
                                                            <ZoomInIcon className="h-3 w-3" />
                                                        </button>
                                                        <button onClick={() => handleDownload(optionImages[idx])} className="p-1 bg-white/20 hover:bg-white/40 text-white rounded-md" title="Tải xuống">
                                                            <ArrowDownTrayIcon className="h-3 w-3" />
                                                        </button>
                                                        <button onClick={() => {
                                                            const newImg = [...optionImages];
                                                            newImg[idx] = '';
                                                            setOptionImages(newImg);
                                                        }} className="p-1 bg-red-500/80 hover:bg-red-500 text-white rounded-md" title="Xóa">
                                                            <TrashIcon className="h-3 w-3" />
                                                        </button>
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex gap-1">
                                                    <button 
                                                        onClick={() => {
                                                            setUploadingOptionIndex(idx);
                                                            optionFileInputRef.current?.click();
                                                        }} 
                                                        className="p-1 text-gray-300 hover:text-teal-500"
                                                        title="Tải ảnh lên"
                                                    >
                                                        <UploadIcon className="h-4 w-4" />
                                                    </button>
                                                    <button 
                                                        onClick={() => handleGenerateOptionImage(idx)}
                                                        className="p-1 text-gray-300 hover:text-purple-500"
                                                        title="Tạo ảnh AI"
                                                    >
                                                        <SparklesIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        <span className="text-xs text-gray-400">Ảnh minh họa (tùy chọn)</span>
                                    </div>
                                </div>
                            ))}
                            <button onClick={handleAddOption} className="text-xs font-bold text-teal-600 flex items-center gap-1 hover:underline">
                                <PlusIcon className="h-3 w-3" /> Thêm lựa chọn
                            </button>
                            <input ref={optionFileInputRef} type="file" className="hidden" accept="image/*" onChange={(e) => uploadingOptionIndex !== null && handleOptionFileUpload(e, uploadingOptionIndex)} />
                        </div>
                    </section>
                </div>

                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-xl border-t">
                    <button onClick={handleClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Hủy</button>
                    <button onClick={handleSave} className="px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700">Lưu thay đổi</button>
                </div>
            </div>
        </div>
    );
};


const EMPTY_MODULE: LearningModule = {
    id: '',
    title: 'Bài học mới',
    content: 'Nội dung bài học mới...',
    status: 'pending',
    question: {
        text: 'Câu hỏi mới?',
        options: ['Lựa chọn 1', 'Lựa chọn 2'],
        correctAnswer: 'Lựa chọn 1'
    }
};

const ProgressRing: React.FC<{ progress: number; size?: number; mdSize?: number; strokeWidth?: number; color?: string; textColor?: string }> = ({
    progress,
    size = 48,
    mdSize,
    strokeWidth = 4,
    color = 'text-teal-600',
    textColor = 'text-slate-700'
}) => {
    // Determine size based on viewport
    const [currentSize, setCurrentSize] = useState(size);
    
    useEffect(() => {
        const updateSize = () => {
            if (window.innerWidth >= 768 && mdSize) {
                setCurrentSize(mdSize);
            } else {
                setCurrentSize(size);
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, [size, mdSize]);

    const radius = (currentSize - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (Math.min(100, Math.max(0, progress)) / 100) * circumference;

    return (
        <div className="relative inline-flex items-center justify-center">
            <svg width={currentSize} height={currentSize} className="transform -rotate-90">
                <circle
                    className="text-white/10"
                    strokeWidth={strokeWidth}
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={currentSize / 2}
                    cy={currentSize / 2}
                />
                <circle
                    className={`${color} transition-all duration-700 ease-out`}
                    strokeWidth={strokeWidth}
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    stroke="currentColor"
                    fill="transparent"
                    r={radius}
                    cx={currentSize / 2}
                    cy={currentSize / 2}
                />
            </svg>
            <span className={`absolute text-xs md:text-xs font-black ${textColor}`}>{Math.round(progress)}%</span>
        </div>
    );
};

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ReactNode; color: string; trend?: string }> = ({ 
    label, 
    value, 
    icon, 
    color,
    trend
}) => (
    <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all flex items-center gap-4 group">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color} shadow-sm group-hover:scale-110 transition-transform`}>
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-0.5 truncate">{label}</p>
            <div className="flex items-baseline gap-2">
                <p className="text-xl font-black text-slate-800 leading-none">{value}</p>
                {trend && <span className="text-xs font-bold text-emerald-500">{trend}</span>}
            </div>
        </div>
    </div>
);

const ChildDashboard: React.FC<ChildDashboardProps> = (props) => {
    const { 
        onBack, 
        onNavigate, 
        userSettings,
        child, 
        isWorkingMode,
        focusSection,
        learningPlans, 
        learningHistory, 
        onSavePlan, 
        onDeletePlan, 
        onCreditUsage, 
        assignments, 
        homeworks, 
        submissions,
        classes,
        students,
        allChildren = [],
        notifications = [],
        joinRequests = [],
        onStudentSubmit,
        onAddNotification,
        onUpdateSettings,
        onRefreshData,
        onNotificationRead,
        isLoading,
        resourceLoadingStates
    } = props;

    const handleSwitchMode = () => {
        // Nếu đang là c hoặc s (làm bài) thì chuyển sang p (phụ huynh), và ngược lại
        const nextWorkingMode = !currentIsWorkingMode;
        onNavigate(View.ChildDashboard, { child, isWorkingMode: nextWorkingMode });
    };

    // Xác định chế độ dựa trên hậu tố URL: p = parent (view only), c/s = child/student (working)
    const urlPath = window.location.pathname;
    const isParentMode = /-p\d+$/.test(urlPath);
    const isChildMode = /-c\d+$/.test(urlPath);
    const isStudentCodeMode = /-s\d+$/.test(urlPath);
    
    // Nguồn sự thật duy nhất cho "Chế độ làm bài"
    const currentIsWorkingMode = isChildMode || isStudentCodeMode;
    
    // Nguồn sự thật duy nhất cho "Chỉ xem"
    // Parent mode allows editing, child/student mode is view-only for metadata/plans
    const isViewOnly = !isParentMode;

    const limits = getUserLimits(userSettings);
    const location = useLocation();
    const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
    const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

    const [editingModule, setEditingModule] = useState<{planId: string, dayIndex: number, moduleIndex: number, module: LearningModule} | null>(null);
    const [focusedModuleId, setFocusedModuleId] = useState<string | null>(null);
    const [activeHomework, setActiveHomework] = useState<Homework | null>(null);
    const [activeAssignmentId, setActiveAssignmentId] = useState<string | null>(null);
    const [activeStudentId, setActiveStudentId] = useState<string | null>(null);
    const [focusedPlanId, setFocusedPlanId] = useState<string | null>(null);

    // Reset states when switching between children to ensure data isolation
    useEffect(() => {
        setActiveHomework(null);
        setActiveAssignmentId(null);
        setActiveStudentId(null);
        setEditingModule(null);
        setFocusedModuleId(null);
        setFocusedPlanId(null);
    }, [child.id]);

    // Lấy thông tin từ sessionStorage ngay khi mount
    useEffect(() => {
        const lastModuleId = sessionStorage.getItem('lastViewedModuleId');
        const lastPlanId = sessionStorage.getItem('lastViewedPlanId');
        
        if (lastModuleId && lastPlanId) {
            setFocusedModuleId(lastModuleId);
            setFocusedPlanId(lastPlanId);
            // Không xóa ngay để useEffect bên dưới có thể dùng dữ liệu này
        }
    }, []);

    // Auto-open homework khi navigate từ "Thông báo từ lớp" với assignmentId
    useEffect(() => {
        const state = location.state as { assignmentId?: string } | null;
        const assignmentId = state?.assignmentId;
        if (!assignmentId || !assignments.length) return;
        const assn = assignments.find(a => String(a.id) === String(assignmentId));
        if (!assn) return;
        setActiveAssignmentId(assn.id);
        setActiveStudentId(currentStudentId);
        const hw = homeworks.find(h => String(h.id) === String(assn.homeworkId));
        if (hw) {
            setActiveHomework({
                ...hw,
                generalInstructions: hw.generalInstructions || (assn as any).instructions || (assn as any).content || '',
                content: hw.content || (assn as any).instructions || (assn as any).content || '',
                attachments: assn.attachments || (hw as any).attachments
            });
        } else {
            const title = (assn as any).title || (assn as any).homeworkTitle || 'Nhiệm vụ';
            const instructions = (assn as any).instructions || '';
            const fakeId = assn.homeworkId && String(assn.homeworkId).trim() ? assn.homeworkId : assn.id;
            setActiveHomework({ id: fakeId, title, content: instructions, generalInstructions: instructions, tasks: [], attachments: assn.attachments } as Homework);
        }
    }, [location.state, assignments, homeworks, child, students]);

    // Scroll đến section tương ứng khi mở từ ParentDashboard (Xem chi tiết tiến độ vs Mở chế độ con làm bài)
    useEffect(() => {
        if (!focusSection) return;
        const scrollToSection = (id: string) => {
            const el = document.getElementById(id);
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
            }
        };
        if (focusSection === 'progress') {
            scrollToSection('child-progress-section');
            const fallback = document.getElementById('child-homework-section');
            if (!document.getElementById('child-progress-section') && fallback) scrollToSection('child-homework-section');
        } else if (focusSection === 'homework') {
            scrollToSection('child-homework-section');
        }
    }, [focusSection, assignments.length]);

    // Thực hiện scroll và highlight khi dữ liệu learningPlans đã sẵn sàng
    useEffect(() => {
        if (!focusedModuleId || !learningPlans || learningPlans.length === 0) return;

        let retryCount = 0;
        const maxRetries = 10;
        
        const tryScroll = () => {
            const element = document.getElementById(`module-${focusedModuleId}`);
            if (element) {
                // Đảm bảo phần tử cha (details) đã mở hoàn toàn
                // scrollIntoView có thể không chính xác nếu đang trong quá trình animation
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    
                    // Hiệu ứng làm nổi bật module vừa học
                    element.classList.add('ring-4', 'ring-teal-500', 'ring-opacity-50', 'scale-[1.02]', 'z-10');
                    
                    // Xóa thông tin sau khi đã thực hiện thành công để tránh scroll lại vô lý
                    sessionStorage.removeItem('lastViewedModuleId');
                    sessionStorage.removeItem('lastViewedPlanId');
                    
                    setTimeout(() => {
                        element.classList.remove('ring-4', 'ring-teal-500', 'ring-opacity-50', 'scale-[1.02]', 'z-10');
                        setFocusedModuleId(null);
                        setFocusedPlanId(null);
                    }, 3000);
                }, 100);
            } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(tryScroll, 200);
            }
        };

        // Bắt đầu thử scroll sau một khoảng thời gian ngắn
        const initialTimer = setTimeout(tryScroll, 300);
        return () => clearTimeout(initialTimer);
    }, [focusedModuleId, learningPlans]); // Phụ thuộc vào focusedModuleId và learningPlans

    if (!child) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
                <Spinner size="lg" />
                <p className="text-gray-500">Không tìm thấy thông tin của con. Vui lòng quay lại và thử lại.</p>
                <button 
                    onClick={onBack}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                    Quay lại
                </button>
            </div>
        );
    }
    
    const handleUpdateModule = (updatedModule: LearningModule) => {
        if (!editingModule) return;
        
        const plan = learningPlans.find(p => p.id == editingModule.planId);
        if (!plan) {
            console.error('Plan not found for update:', editingModule.planId);
            return;
        }
        
        const updatedPlan = JSON.parse(JSON.stringify(plan)) as ChildLearningPlan;
        if ((updatedPlan as any).daily_plans) delete (updatedPlan as any).daily_plans;
        
        if (editingModule.moduleIndex === -1) {
            // Thêm module mới thủ công
            const newModule = {
                ...updatedModule,
                id: updatedModule.id || `mod-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
            };
            updatedPlan.dailyPlans[editingModule.dayIndex].modules.push(newModule);
        } else {
            // Cập nhật module hiện có
            updatedPlan.dailyPlans[editingModule.dayIndex].modules[editingModule.moduleIndex] = updatedModule;
        }
        
        if (typeof props.onUpdatePlan === 'function') {
            props.onUpdatePlan(updatedPlan);
        }
        setEditingModule(null);
    };

    const handleDeleteModule = (planId: string, dayIndex: number, moduleIndex: number) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa bài học này không?')) return;
        
        const plan = learningPlans.find(p => p.id == planId);
        if (!plan) return;
        
        const updatedPlan = JSON.parse(JSON.stringify(plan)) as ChildLearningPlan;
        if ((updatedPlan as any).daily_plans) delete (updatedPlan as any).daily_plans;
        
        updatedPlan.dailyPlans[dayIndex].modules.splice(moduleIndex, 1);
        
        if (typeof props.onUpdatePlan === 'function') {
            props.onUpdatePlan(updatedPlan);
        }
    };
    
    /** Helper: match child với student (dùng chung cho childAssignments, childClassNotifications, childResultsByClass) */
    const resolveStudentForChild = useMemo(() => {
        const childStudentId = child.studentId ?? (child as any).student_id;
        const norm = (s: string) => (s || '').trim().toLowerCase();
        
        // 1. ƯU TIÊN TUYỆT ĐỐI: Lấy studentId từ joinRequests đã được phê duyệt
        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id ?? r.childId) === String(child.id));
        const approvedReq = childReqs.find(r => (r.student_id ?? r.studentId) && r.status === 'approved');
        const studentIdFromReq = approvedReq?.student_id ?? approvedReq?.studentId;

        if (studentIdFromReq != null) {
            const match = students.find(s => String(s.id) === String(studentIdFromReq));
            if (match) return match;
        }

        // 2. ƯU TIÊN 2: Khớp bằng ID chính xác (nếu có)
        if (childStudentId != null) {
            const match = students.find(s => String(s.id) === String(childStudentId) || (s.studentId && String(s.studentId) === String(childStudentId)));
            if (match) return match;
        }

        // 3. ƯU TIÊN 3: Khớp bằng Tên (chỉ khi có ít nhất 1 sự tự tin về dữ liệu)
        let studentProfile = students.find(s => 
            (child.name && s.name && norm(child.name) === norm(s.name)) ||
            (child.realName && s.realName && norm(child.realName) === norm(s.realName))
        );

        // 4. FALLBACK: Nếu chỉ có 1 con và 1 học sinh, có thể tạm chấp nhận (nhưng phải cẩn thận)
        if (!studentProfile && allChildren?.length === 1 && students.length === 1) {
            studentProfile = students[0];
        }

        return studentProfile ?? null;
    }, [child, students, joinRequests, allChildren]);

    /** ID DUY NHẤT để khớp dữ liệu (Strict Student Identification) */
    const currentStudentId = useMemo(() => {
        const studentProfile = resolveStudentForChild;
        const studentIdStr = studentProfile ? String(studentProfile.id) : null;
        
        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id ?? r.childId) === String(child.id));
        const approvedReq = childReqs.find(r => (r.student_id ?? r.studentId) && r.status === 'approved');
        const studentIdFromReq = approvedReq?.student_id ?? approvedReq?.studentId;
        
        return studentIdStr || (studentIdFromReq ? String(studentIdFromReq) : null);
    }, [resolveStudentForChild, joinRequests, child.id]);

    const childAssignments = useMemo(() => {
        const effectiveStudentIdStr = currentStudentId;

        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id ?? r.childId) === String(child.id));
        const approvedClassIdsFromRequests = childReqs
            .filter((r: any) => r.status === 'approved')
            .map((r: any) => String(r.class_id ?? r.classId));

        const myClasses = classes.filter(c => 
            (effectiveStudentIdStr && (c.studentIds || []).some((sid: any) => String(sid) === String(effectiveStudentIdStr))) ||
            approvedClassIdsFromRequests.includes(String(c.id))
        );
        
        const myClassIds = Array.from(new Set([
            ...myClasses.map(c => String(c.id)),
            ...approvedClassIdsFromRequests
        ]));
        
        const uniqueAssignments = Array.from(new Map(assignments.map(a => [String(a.id), a])).values());

        return uniqueAssignments
            .filter(a => {
                const aClassId = String(a.classId ?? (a as any).class_id ?? '');
                const isMyClass = myClassIds.includes(aClassId);
                
                // Đảm bảo bài tập này dành riêng cho học sinh này (hoặc cho cả lớp)
                const aStudentIds = (a.studentIds || (a as any).student_ids || []) as any[];
                const isForMe = !effectiveStudentIdStr || aStudentIds.length === 0 || aStudentIds.some((sid: any) => String(sid) === String(effectiveStudentIdStr));
                const isStarted = !a.scheduledDate || new Date(a.scheduledDate) <= new Date();
                
                return isMyClass && isForMe && isStarted;
            })
            .map(assignment => {
                const hwId = assignment.homeworkId || assignment.id;
                const homework = homeworks.find(h => String(h.id) === String(hwId));
                
                // Tìm submission CHÍNH XÁC theo cặp (assignmentId, studentId)
                const submission = submissions.find(s => 
                    String(s.assignmentId) === String(assignment.id) && 
                    (effectiveStudentIdStr ? String(s.studentId) === String(effectiveStudentIdStr) : false)
                );
                
                const isExpired = new Date(assignment.dueDate).getTime() < new Date().getTime();
                const canSubmit = !isExpired || assignment.allowLateSubmission;
                const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                const instructions = homework?.generalInstructions || (assignment as any).instructions || (assignment as any).content || '';

                return {
                    ...assignment,
                    title: (assignment as any).homeworkTitle || (assignment as any).homework_title || homework?.title || assignment.title || 'Bài tập không xác định',
                    className: cls?.name || 'Lớp học',
                    status: submission?.status || SUBMISSION_STATUS.CHUA_NOP,
                    grade: submission?.grade,
                    teacherComments: submission?.teacherComments,
                    canSubmit,
                    isExpired,
                    instructions,
                    submissionType: assignment.submissionType || (assignment as any).submission_type
                };
            });
    }, [assignments, homeworks, submissions, currentStudentId, classes, allChildren, students, joinRequests, child.id]);

    const childClassNotifications = useMemo(() => {
        type NotifItem = { 
            type: 'assignment' | 'graded' | 'notification'; 
            title: string; 
            subtitle: string; 
            class?: Class; 
            date: string; 
            assignment?: Assignment; 
            submission?: Submission; 
            notification?: Notification;
            isRead?: boolean;
            statusLabel?: string;
            statusColor?: string;
            isExpired?: boolean;
        };
        const items: NotifItem[] = [];
        
        const effectiveStudentIdStr = currentStudentId;
        
        // Helper để lấy trạng thái bài tập
        const getAssignmentStatus = (assn: Assignment, studentId: string) => {
            const sub = submissions.find(s => String(s.assignmentId) === String(assn.id) && String(s.studentId) === studentId);
            if (assn.type === 'announcement') {
                if (!sub) return { label: 'Chưa xem', color: 'bg-teal-50 text-teal-600 border-teal-100' };
                return { label: 'Đã xem', color: 'bg-slate-50 text-slate-600 border-slate-100' };
            }
            if (!sub) return { label: 'Chưa làm', color: 'bg-rose-50 text-rose-600 border-rose-100' };
            if (isSubmissionGraded(sub.status)) return { label: 'Đã chấm', color: 'bg-emerald-50 text-emerald-600 border-emerald-100' };
            if (isSubmissionSubmitted(sub.status)) return { label: 'Đã nộp', color: 'bg-blue-50 text-blue-600 border-blue-100' };
            return { label: 'Đang làm', color: 'bg-amber-50 text-amber-600 border-amber-100' };
        };

        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id ?? r.childId) === String(child.id));
        const approvedClassIds = childReqs.filter(r => r.status === 'approved').map((r: any) => String(r.class_id ?? r.classId));
        const myClasses = classes.filter(c => (effectiveStudentIdStr && (c.studentIds || []).some((sid: any) => String(sid) === effectiveStudentIdStr)) || approvedClassIds.includes(String(c.id)));
        const myClassIds = myClasses.map(c => String(c.id));

        // 1. Lấy từ Assignments & Submissions thuộc các lớp của con
        myClasses.forEach(cls => {
            const clsIdStr = String(cls.id);
            const getAssnStudentIds = (assn: any) => (assn.studentIds || assn.student_ids || []) as any[];
            const isForStudent = (assn: any) => { const ids = getAssnStudentIds(assn); return ids.length === 0 || !effectiveStudentIdStr || ids.some((sid: any) => String(sid) === effectiveStudentIdStr); };
            
            assignments.filter(a => String(a.classId ?? (a as any).class_id) === clsIdStr && a.status !== 'recalled' && isForStudent(a)).forEach(assn => {
                const hw = homeworks.find(h => h.id === assn.homeworkId);
                const typeLabel = assn.type === 'announcement' ? 'Thông báo' : assn.type === 'task' ? 'Nhiệm vụ' : 'Bài tập';
                const assnTitle = (assn as any).homeworkTitle || (assn as any).homework_title || assn.title || hw?.title || typeLabel;
                const status = effectiveStudentIdStr ? getAssignmentStatus(assn, effectiveStudentIdStr) : { label: 'Chưa xem', color: 'bg-teal-50 text-teal-600' };
                const dueMs = assn.dueDate && String(assn.dueDate).trim() ? new Date(assn.dueDate).getTime() : 0;
                const isExpired = dueMs > 0 && dueMs < Date.now();
                
                items.push({ 
                    type: 'assignment', 
                    title: assnTitle, 
                    subtitle: cls.name, 
                    class: cls, 
                    date: assn.assignedDate || (assn as any).assigned_date || '', 
                    assignment: assn,
                    isRead: false,
                    statusLabel: status.label,
                    statusColor: status.color,
                    isExpired
                });
            });

            if (effectiveStudentIdStr) {
                submissions.filter(s => String(s.studentId) === effectiveStudentIdStr && isSubmissionGraded(s.status)).forEach(sub => {
                    const assn = assignments.find(a => a.id === sub.assignmentId);
                    if (!assn || String(assn.classId || (assn as any).class_id) !== String(cls.id)) return;
                    const hw = homeworks.find(h => h.id === assn.homeworkId);
                    const gradedTitle = (assn as any).homeworkTitle || (assn as any).homework_title || hw?.title || assn.title || 'Bài tập';
                    const dueMs = assn.dueDate && String(assn.dueDate).trim() ? new Date(assn.dueDate).getTime() : 0;
                    const isExpired = dueMs > 0 && dueMs < Date.now();
                    items.push({ 
                        type: 'graded', 
                        title: `Đã chấm: ${gradedTitle}`, 
                        subtitle: cls.name, 
                        class: cls, 
                        date: sub.submittedDate || (sub as any).submitted_date || '', 
                        submission: sub, 
                        assignment: assn,
                        isRead: false,
                        statusLabel: 'Đã chấm',
                        statusColor: 'bg-emerald-50 text-emerald-600 border-emerald-100',
                        isExpired
                    });
                });
            }
        });

        // 2. Lấy từ Notifications - lọc kỹ hơn theo con
        notifications.filter(n => {
            const isTargetType = (n as any).type === 'assignment' || n.icon === 'homework' || n.icon === 'task' || n.icon === 'announcement';
            if (!isTargetType) return false;
            
            const msg = (n.message || '');
            
            // ƯU TIÊN 1: Nếu notification có metadata chính xác
            const nChildId = (n as any).childId || (n as any).child_id;
            const nClassId = (n as any).classId || (n as any).class_id;
            const nStudentId = (n as any).studentId || (n as any).student_id;
            
            if (nChildId && String(nChildId) !== String(child.id)) return false;
            if (nClassId && !myClassIds.includes(String(nClassId))) return false;
            if (nStudentId && effectiveStudentIdStr && String(nStudentId) !== effectiveStudentIdStr) return false;

            // ƯU TIÊN 2: Nếu không có metadata, lọc theo tên con để tránh nhầm lẫn phụ huynh có nhiều con
            const otherChildren = (allChildren || []).filter(c => c.id !== child.id);
            const hasOtherChildName = otherChildren.some(oc => oc.name && msg.includes(oc.name));
            const hasThisChildName = child.name && msg.includes(child.name);
            
            if (hasOtherChildName && !hasThisChildName) return false;
            
            return msg.includes('lớp');
        }).forEach(notif => {
            const msg = notif.message || '';
            const m = msg.match(/trong lớp\s+(.+?)(?:\.|$)/) || msg.match(/lớp\s+(.+?)(?:\s|\.|$)/);
            const className = m ? m[1].trim() : '';
            const cls = classes.find(c => c.name === className || c.name?.includes(className) || String(c.id) === String((notif as any).classId));
            
            // Nếu match được lớp, kiểm tra xem con có trong lớp đó không
            if (cls && !myClassIds.includes(String(cls.id))) return;

            const titleMatch = msg.match(/giao:\s*(.+?)\s+trong lớp/) || msg.match(/(.+?)\s+trong lớp/);
            const title = titleMatch ? titleMatch[1].trim() : msg.slice(0, 60);
            
            const assn = assignments.find(a => 
                (a.id === (notif as any).assignmentId || a.title === title || (a as any).homeworkTitle === title || (a as any).homework_title === title || a.instructions?.includes(title)) && 
                (!cls || String(a.classId ?? (a as any).class_id) === String(cls.id))
            );

            if (cls || className) {
                const type: 'assignment' | 'notification' = assn ? 'assignment' : 'notification';
                const status = assn && effectiveStudentIdStr ? getAssignmentStatus(assn, effectiveStudentIdStr) : null;
                const dueMs = assn?.dueDate && String(assn.dueDate).trim() ? new Date(assn.dueDate).getTime() : 0;
                const isExpired = dueMs > 0 && dueMs < Date.now();
                
                items.push({ 
                    type, 
                    title, 
                    subtitle: cls?.name || className || 'Thông báo', 
                    class: cls, 
                    date: notif.timestamp || '', 
                    notification: notif, 
                    assignment: assn,
                    isRead: notif.isRead || false,
                    statusLabel: status?.label || (notif.isRead ? 'Đã xem' : 'Chưa xem'),
                    statusColor: status?.color || (notif.isRead ? 'bg-slate-50 text-slate-500' : 'bg-teal-50 text-teal-600'),
                    isExpired: assn ? isExpired : undefined
                });
            }
        });
        
        // Loại bỏ trùng lặp (nếu cùng title và date)
        const seen = new Set();
        const uniqueItems = items.filter(item => {
            const key = `${item.title}-${item.date}-${item.subtitle}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

        return uniqueItems.sort((a, b) => (new Date(b.date || 0).getTime()) - (new Date(a.date || 0).getTime())).slice(0, 10);
    }, [resolveStudentForChild, classes, assignments, homeworks, submissions, notifications, child, allChildren, joinRequests]);

    /** Kết quả học tập theo từng lớp - để phụ huynh xem */
    const childResultsByClass = useMemo(() => {
        const effectiveStudentIdStr = currentStudentId;
        
        // Lấy danh sách classId từ joinRequests
        const childReqs = (joinRequests || []).filter((r: any) => String(r.child_id ?? r.childId) === String(child.id));
        const approvedClassIds = childReqs.filter(r => r.status === 'approved').map((r: any) => String(r.class_id ?? r.classId));
        
        const myClasses = classes.filter(c =>
            (effectiveStudentIdStr && (c.studentIds || []).some((sid: any) => String(sid) === effectiveStudentIdStr)) ||
            approvedClassIds.includes(String(c.id))
        );

        return myClasses.map(cls => {
            const clsIdStr = String(cls.id);
            const classAssignments = assignments.filter(a => {
                const aClassId = String(a.classId ?? (a as any).class_id ?? '');
                if (aClassId !== clsIdStr || a.status === 'recalled') return false;
                const aStudentIds = (a.studentIds || (a as any).student_ids || []) as any[];
                return !effectiveStudentIdStr || aStudentIds.length === 0 || aStudentIds.some((sid: any) => String(sid) === String(effectiveStudentIdStr));
            });
            const classSubmissions = submissions.filter(s =>
                (effectiveStudentIdStr && String(s.studentId) === String(effectiveStudentIdStr)) &&
                classAssignments.some(a => String(a.id) === String(s.assignmentId))
            );
            const completed = classAssignments.filter(a =>
                classSubmissions.some(s => String(s.assignmentId) === String(a.id))
            ).length;
            const gradedSubs = classSubmissions.filter(s => isSubmissionGraded(s.status) && s.grade != null);
            const avgGrade = gradedSubs.length > 0
                ? (gradedSubs.reduce((acc, s) => acc + (parseFloat(String(s.grade)) || 0), 0) / gradedSubs.length).toFixed(1)
                : null;

            const clsSessions = cls.sessions || [];
            const mySessions = effectiveStudentIdStr ? clsSessions.filter((s: any) => (s.grades || []).some((g: any) => String(g.studentId) === effectiveStudentIdStr)) : [];
            
            // Lấy danh sách chi tiết các buổi học có điểm/nhận xét
            const sessionDetails = mySessions.map((s: any) => {
                const gradeInfo = (s.grades || []).find((g: any) => String(g.studentId) === effectiveStudentIdStr);
                return {
                    id: s.id,
                    title: s.title || `Buổi học ${new Date(s.date).toLocaleDateString('vi-VN')}`,
                    date: s.date,
                    grade: gradeInfo?.grade,
                    attendance: gradeInfo?.attendance,
                    comments: gradeInfo?.comments || gradeInfo?.teacherComments
                };
            }).filter(s => s.grade != null || s.comments || s.attendance);

            const sessionAvgGrade = mySessions.filter((s: any) => {
                const g = (s.grades || []).find((gr: any) => String(gr.studentId) === effectiveStudentIdStr);
                return g && g.grade !== '' && g.grade != null;
            }).length > 0
                ? (mySessions
                    .map((s: any) => (s.grades || []).find((gr: any) => String(gr.studentId) === effectiveStudentIdStr)?.grade)
                    .filter((g: any) => g !== '' && g != null)
                    .reduce((acc: number, g: any) => acc + (parseFloat(String(g)) || 0), 0) /
                    mySessions.filter((s: any) => {
                        const g = (s.grades || []).find((gr: any) => String(gr.studentId) === effectiveStudentIdStr);
                        return g && g.grade !== '' && g.grade != null;
                    }).length).toFixed(1)
                : null;
            const attendance = mySessions.length > 0
                ? (mySessions.filter((s: any) => (s.grades || []).find((gr: any) => String(gr.studentId) === effectiveStudentIdStr)?.attendance === 'present').length / mySessions.length * 100).toFixed(0)
                : null;

            return {
                class: cls,
                totalAssignments: classAssignments.length,
                completed,
                avgGrade,
                sessionAvgGrade,
                attendance,
                sessionDetails
            };
        });
    }, [resolveStudentForChild, classes, assignments, submissions, allChildren, students, joinRequests, child.id]);

    const statsSummary = useMemo(() => {
        const totalAssn = childAssignments.length;
        const completedAssn = childAssignments.filter(a => isSubmissionSubmitted(a.status)).length;
        const completionRate = totalAssn > 0 ? (completedAssn / totalAssn) * 100 : 0;
        
        const gradedAssn = childAssignments.filter(a => isSubmissionGraded(a.status) && a.grade != null);
        const avgGrade = gradedAssn.length > 0 
            ? (gradedAssn.reduce((sum, a) => sum + (parseFloat(String(a.grade)) || 0), 0) / gradedAssn.length).toFixed(1)
            : '0.0';

        const totalClasses = childResultsByClass.length;
        const avgAttendance = childResultsByClass.length > 0
            ? (childResultsByClass.reduce((sum, c) => sum + (parseFloat(c.attendance || '0') || 0), 0) / childResultsByClass.length).toFixed(0)
            : '0';

        return {
            totalAssn,
            completedAssn,
            completionRate,
            avgGrade,
            totalClasses,
            avgAttendance
        };
    }, [childAssignments, childResultsByClass]);

    const getPlanStatus = (plan: ChildLearningPlan): PlanStatus => {
        const allModules = plan.dailyPlans.flatMap(d => d.modules);
        if (allModules.length === 0) return PlanStatus.OnTrack;

        const completedCount = allModules.filter(m => m.status === 'completed').length;
        if (completedCount === allModules.length) return PlanStatus.Completed;

        const threeDaysAgo = new Date();
        threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
        if (new Date(plan.createdAt) < threeDaysAgo && completedCount === 0) {
            return PlanStatus.NeedsAttention;
        }
        return PlanStatus.OnTrack;
    };

    const statusInfo = {
        [PlanStatus.OnTrack]: { text: 'Đúng tiến độ', color: 'blue', icon: <PlayIcon className="h-4 w-4" /> },
        [PlanStatus.NeedsAttention]: { text: 'Cần chú ý', color: 'yellow', icon: <ClockIcon className="h-4 w-4" /> },
        [PlanStatus.Completed]: { text: 'Đã hoàn thành', color: 'green', icon: <CheckBadgeIcon className="h-4 w-4" /> },
    };

    const colorClasses = {
        blue: 'bg-blue-100 text-blue-800',
        yellow: 'bg-yellow-100 text-yellow-800',
        green: 'bg-green-100 text-green-800',
    };

    const isFromParent = userSettings?.role === 'parent';
    const isHomeworkMode = isFromParent && focusSection === 'homework';

    // 1. Định danh student profile chính xác cho Child hiện tại
    const studentProfile = resolveStudentForChild;
    const showViewOnlyWarning = isViewOnly;

    const incompleteItems = useMemo(() => childAssignments.filter(a => !isSubmissionSubmitted(a.status) && (a as any).type !== 'announcement'), [childAssignments]);
    const childAnnouncements = useMemo(() => childAssignments.filter(a => (a as any).type === 'announcement' && !isSubmissionSubmitted(a.status)), [childAssignments]);
    const completedItems = useMemo(() => childAssignments.filter(a => isSubmissionSubmitted(a.status)), [childAssignments]);

    if (isLoading && (!childAssignments.length && !classes.length)) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12 min-h-screen">
                <ChildDashboardSkeleton />
            </div>
        );
    }

    if (activeHomework) {
        // 1. Xác định chính xác ID của học sinh đang thực hiện (ưu tiên ID được set khi click)
        const effectiveStudentId = activeStudentId || currentStudentId;
        
        // 2. Tìm assignment tương ứng cho học sinh này
        const myClasses = effectiveStudentId ? classes.filter(c => (c.studentIds || []).some((sid: any) => String(sid) === String(effectiveStudentId))) : [];
        const assignment = assignments.find(a => 
            (String(a.homeworkId) === String(activeHomework.id) || String(a.id) === String(activeHomework.id)) && 
            (myClasses.some(c => String(c.id) === String(a.classId ?? (a as any).class_id)) || !a.studentIds || (a.studentIds || []).some((sid: any) => String(sid) === String(effectiveStudentId)))
        );
        
        const assignmentId = activeAssignmentId || assignment?.id;
        
        // 3. Tìm submission CUỐI CÙNG của học sinh này (tránh làm sai lần đầu để xem đáp án)
        const submission = assignmentId && effectiveStudentId ? getLastSubmission(submissions, assignmentId, effectiveStudentId) ?? submissions.find(s => 
            String(s.assignmentId) === String(assignmentId) && 
            String(s.studentId) === String(effectiveStudentId)
        ) : null;
        
        /** Chỉ hiển thị kết quả khi: (1) chế độ xem, (2) GV đã chấm, HOẶC (3) đã nộp VÀ showResultToStudent bật */
        const showResults = isViewOnly || isSubmissionGraded(submission?.status) || (isSubmissionSubmitted(submission?.status) && !!(activeHomework as any).showResultToStudent);
        const assignType = (assignment as any)?.type;
        const dueDateMs = assignment?.dueDate && String(assignment.dueDate).trim() ? new Date(assignment.dueDate).getTime() : 0;
        const isExpired = dueDateMs > 0 && dueDateMs < Date.now();
        const canSubmit = !isExpired || !!assignment?.allowLateSubmission;

        return (
            <HomeworkViewer 
                key={`${activeHomework.id}-${effectiveStudentId}`}
                homework={activeHomework} 
                onClose={() => { setActiveHomework(null); setActiveAssignmentId(null); setActiveStudentId(null); }} 
                showResultsInitially={showResults} 
                initialAnswers={normalizeAnswersForTasks(activeHomework?.tasks || [], parseSubmissionContent(submission))}
                timeLimitMinutes={assignment?.timeLimitMinutes}
                assignmentType={assignType === 'task' ? 'task' : assignType === 'announcement' ? 'announcement' : 'homework'}
                submissionType={assignment?.submissionType}
                user={userSettings}
                canSubmitByDeadline={canSubmit}
                isExpired={isExpired}
                isReadOnly={isParentMode}
                teacherGrade={isSubmissionGraded(submission?.status) ? submission?.grade : undefined}
                teacherComments={isSubmissionGraded(submission?.status) ? submission?.teacherComments : undefined}
                gradingSystem={assignment?.gradingSystem}
                renderExtraHeaderActions={(isParentMode || (userSettings?.role === 'parent' && showResults)) ? () => (
                    <button 
                        onClick={handleSwitchMode}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl font-black text-[10px] uppercase tracking-wider transition-all shadow-sm border ${
                            currentIsWorkingMode ? 'bg-rose-50 text-rose-600 border-rose-100 hover:bg-rose-100' : 'bg-teal-50 text-teal-600 border-teal-100 hover:bg-teal-100'
                        }`}
                    >
                        {currentIsWorkingMode ? (
                            <>
                                <XMarkIcon className="h-4 w-4" />
                                Thoát chế độ con học
                            </>
                        ) : (
                            <>
                                <PlayIcon className="h-4 w-4" />
                                Mở chế độ cho con học
                            </>
                        )}
                    </button>
                ) : undefined}
                onComplete={(isViewOnly || assignType === 'announcement' || !canSubmit) ? undefined : (results) => {
                    if (assignmentId && effectiveStudentId) {
                        onStudentSubmit(assignmentId, String(effectiveStudentId), results);
                    }
                    setActiveHomework(null);
                    setActiveAssignmentId(null);
                    setActiveStudentId(null);
                }}
            />
        );
    }

    return (
        <div className="space-y-6 pb-20">
            {isPlanModalOpen && <LearningPlanModal child={child} student={studentProfile || undefined} onClose={() => setIsPlanModalOpen(false)} onSave={onSavePlan} onCreditUsage={onCreditUsage} />}
            <HistoryModal isOpen={isHistoryModalOpen} onClose={() => setIsHistoryModalOpen(false)} history={learningHistory} plans={learningPlans} />
            {editingModule && <ModuleEditModal module={editingModule.module} onClose={() => setEditingModule(null)} onSave={handleUpdateModule} onCreditUsage={onCreditUsage} />}
            
            {/* Context Banners - Single Source of Truth from URL */}
            {isParentMode && (
                <div className="flex items-center justify-between gap-4 p-4 rounded-3xl bg-slate-800 text-white shadow-xl border border-slate-700 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 backdrop-blur-md">
                            <ChartBarIcon className="h-6 w-6 text-teal-400" />
                        </div>
                        <div>
                            <p className="text-sm font-black uppercase tracking-widest text-teal-400">Chế độ xem chi tiết (Chỉ đọc)</p>
                            <p className="text-xs text-slate-300">Bạn đang xem tiến độ và kết quả của {child.name}. Không thể làm bài hoặc sửa đổi.</p>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleSwitchMode} className="px-5 py-2.5 text-xs font-black bg-teal-500 text-white rounded-xl hover:bg-teal-400 transition-all active:scale-95 shadow-lg shadow-teal-500/20">
                            Chuyển sang chế độ con học
                        </button>
                        <button onClick={onBack} className="px-5 py-2.5 text-xs font-black bg-white/10 text-white rounded-xl hover:bg-white/20 transition-all active:scale-95 border border-white/10">
                            Quay lại
                        </button>
                    </div>
                </div>
            )}
            
            {currentIsWorkingMode && (
                <div className="flex items-center justify-between gap-4 p-4 rounded-3xl bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-xl animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center shrink-0 backdrop-blur-md border border-white/20">
                            <AcademicCapIcon className="h-6 w-6" />
                        </div>
                        <div>
                            <p className="text-sm font-black uppercase tracking-widest text-teal-100">Chế độ con làm bài/nhiệm vụ</p>
                            <p className="text-xs text-teal-50">Hãy đưa máy cho {child.name} để con bắt đầu học và làm bài tập/nhiệm vụ nhé!</p>
                        </div>
                    </div>
                    <button onClick={handleSwitchMode} className="px-5 py-2.5 text-xs font-black bg-white text-teal-700 rounded-xl hover:bg-teal-50 transition-all active:scale-95 shadow-lg">
                        Thoát chế độ
                    </button>
                </div>
            )}

            {/* Intelligent Welcome Banner */}
            <div className="relative overflow-hidden rounded-[2rem] lg:rounded-[2.5rem] bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 lg:p-8 text-white shadow-2xl border border-slate-700/50">
                <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl -mr-20 -mt-20 animate-pulse"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -ml-20 -mb-20"></div>
                
                <div className="relative flex flex-row items-center gap-4 lg:gap-8">
                    {/* Child Avatar & Name Section */}
                    <div className="flex flex-row items-center gap-3 lg:gap-6 flex-1 min-w-0">
                        <div className="relative shrink-0">
                            <div className="w-12 h-12 md:w-16 md:h-16 lg:w-20 lg:h-20 rounded-xl lg:rounded-[2rem] bg-gradient-to-br from-teal-400 to-blue-500 p-1 shadow-2xl rotate-3">
                                <div className="w-full h-full rounded-[0.9rem] lg:rounded-[1.8rem] bg-slate-800 flex items-center justify-center overflow-hidden border-2 border-white/10">
                                    {child.avatarUrl ? (
                                        <img src={formatAssetUrl(child.avatarUrl)} alt={child.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="text-lg md:text-2xl lg:text-3xl">
                                            {child.gender === 'male' ? '👦' : child.gender === 'female' ? '👧' : '🧒'}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="absolute -bottom-1 -right-1 w-5 h-5 md:w-7 md:h-7 lg:w-8 lg:h-8 rounded-lg lg:rounded-xl bg-white text-slate-900 flex items-center justify-center shadow-lg font-black text-xs md:text-xs lg:text-xs -rotate-6">
                                {child.age}t
                            </div>
                        </div>
                        <div className="text-left min-w-0 flex-1">
                            <h2 className="text-sm md:text-xl lg:text-2xl font-black tracking-tight leading-tight line-clamp-2 md:line-clamp-3 mb-1">{getDashboardGreeting('child', child.name)}</h2>
                            <p className="text-slate-400 font-bold flex items-center gap-1.5 text-xs md:text-xs lg:text-xs uppercase tracking-tight">
                                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                                {statsSummary.totalClasses} lớp • {statsSummary.totalAssn} bài tập & nhiệm vụ
                            </p>
                        </div>
                    </div>

                    {/* Quick Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 lg:gap-3 shrink-0">
                        <div className="bg-white/5 backdrop-blur-xl rounded-xl lg:rounded-3xl p-1.5 lg:p-3 border border-white/10 flex flex-col items-center justify-center text-center group hover:bg-white/10 transition-all min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
                            <p className="text-[10px] lg:text-xs font-black text-teal-400 uppercase tracking-widest mb-1 lg:mb-2">Xong</p>
                            <ProgressRing progress={statsSummary.completionRate} size={28} mdSize={36} lgSize={48} color="text-teal-400" textColor="text-white" />
                        </div>
                        <div className="bg-white/5 backdrop-blur-xl rounded-xl lg:rounded-3xl p-1.5 lg:p-3 border border-white/10 flex flex-col items-center justify-center text-center group hover:bg-white/10 transition-all min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
                            <p className="text-[10px] lg:text-xs font-black text-blue-400 uppercase tracking-widest mb-0.5 leading-none">Điểm TB</p>
                            <p className="text-sm md:text-lg lg:text-xl font-black text-white leading-none mt-1">{statsSummary.avgGrade}</p>
                        </div>
                        <div className="bg-white/5 backdrop-blur-xl rounded-xl lg:rounded-3xl p-1.5 lg:p-3 border border-white/10 flex flex-col items-center justify-center text-center group hover:bg-white/10 transition-all min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
                            <p className="text-[10px] lg:text-xs font-black text-purple-400 uppercase tracking-widest mb-0.5 leading-none text-center">C.Cần</p>
                            <p className="text-sm md:text-lg lg:text-xl font-black text-white leading-none mt-1">{statsSummary.avgAttendance}%</p>
                        </div>
                        <div className="bg-white/5 backdrop-blur-xl rounded-xl lg:rounded-3xl p-1.5 lg:p-3 border border-white/10 flex flex-col items-center justify-center text-center group hover:bg-white/10 transition-all min-w-[60px] md:min-w-[80px] lg:min-w-[100px]">
                            <p className="text-[10px] lg:text-xs font-black text-amber-400 uppercase tracking-widest mb-0.5 leading-none">Tin mới</p>
                            <p className="text-sm md:text-lg lg:text-xl font-black text-white leading-none mt-1">{childClassNotifications.length}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                {/* Left Column (Main Activities) */}
                <div className="lg:col-span-8 xl:col-span-9 space-y-8">
                    
                    <section className="space-y-4">
                        <div className="flex items-center justify-between px-2">
                            <h3 className="text-sm lg:text-base font-black text-slate-800 uppercase tracking-widest flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-teal-100 flex items-center justify-center">
                                    <ClockIcon className="h-3.5 w-3.5 text-teal-600" />
                                </div>
                                Bảng tin & Hoạt động
                            </h3>
                            <div className="flex items-center gap-2">
                                {childClassNotifications.filter(n => !n.isRead && n.type === 'notification').length > 0 && (
                                    <span className="px-2 py-1 bg-rose-100 text-rose-600 rounded-lg text-xs lg:text-xs font-black uppercase animate-pulse">
                                        {childClassNotifications.filter(n => !n.isRead && n.type === 'notification').length} Tin mới
                                    </span>
                                )}
                                <span className="hidden sm:inline-block px-3 py-1 bg-slate-100 rounded-full text-xs font-black text-slate-500 uppercase">Mới nhất</span>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
                    {childClassNotifications.length === 0 ? (
                                isLoading || resourceLoadingStates?.core || resourceLoadingStates?.teacher ? (
                                    [1, 2, 3].map(i => (
                                        <div key={i} className="h-24 bg-slate-50 rounded-[1.8rem] border border-slate-100 animate-pulse"></div>
                                    ))
                                ) : (
                                    <div className="md:col-span-2 p-10 bg-slate-50 rounded-[2.5rem] border border-dashed border-slate-200 text-center">
                                        <p className="text-slate-400 font-bold">Chưa có thông báo hoặc hoạt động mới.</p>
                                    </div>
                                )
                    ) : (
                    childClassNotifications.map((item, i) => {
                        const isGraded = item.type === 'graded';
                        const assnType = item.assignment?.type;
                                    const canOpen = !!item.assignment;
                        const typeColor = getTypeColorConfig(isGraded ? undefined : assnType, isGraded);
                                    const isNewNotif = !item.isRead && item.type === 'notification';
                                    
                        return (
                            <button
                                key={i}
                                            onClick={() => {
                                                const assn = item.assignment;
                                                const notif = item.notification;
                                                if (!assn && !notif) return;
                                                
                                                if (assn) {
                                                    const cls = classes.find(c => String(c.id) === String(assn.classId || (assn as any).class_id));
                                                    onNavigate(View.AssignmentDetail, { 
                                                        assignmentId: assn.id, 
                                                        title: assn.title, 
                                                        classCode: cls?.inviteCode, 
                                                        studentId: currentStudentId,
                                                        isWorkingMode: currentIsWorkingMode
                                                    });
                                                } else if (notif) {
                                                    // Mark as read if possible
                                                    if (onNotificationRead) onNotificationRead(notif.id);

                                                    setActiveHomework({ 
                                                        id: `notif_${notif.id}`, 
                                                        title: item.title, 
                                                        content: notif.message, 
                                                        generalInstructions: notif.message, 
                                                        tasks: [],
                                                        attachments: item.assignment?.attachments
                                                    } as Homework);
                                                }
                                            }}
                                            className={`group relative w-full text-left flex items-center gap-3 p-3.5 rounded-[1.8rem] border transition-all ${isNewNotif ? `${typeColor.bg} ${typeColor.border} shadow-md ring-1 ring-inset ${typeColor.border.replace('border-', 'ring-')}/20 scale-[1.02]` : 'bg-white border-slate-100 shadow-sm'} hover:shadow-lg hover:scale-[1.01] active:scale-[0.98]`}
                                        >
                                            <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center shrink-0 ${typeColor.badge} shadow-sm group-hover:scale-110 transition-transform relative`}>
                                                {isGraded ? <CheckBadgeIcon className="h-4 w-4 md:h-5 md:w-5" /> : (item.type === 'notification' ? <BellIcon className="h-4 w-4 md:h-5 md:w-5" /> : <ClipboardDocumentListIcon className="h-4 w-4 md:h-5 md:w-5" />)}
                                                {isNewNotif && (
                                                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-rose-500 rounded-full border-2 border-white animate-pulse shadow-sm"></span>
                                                )}
                                </div>
                                <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 mb-0.5 min-w-0">
                                                    <span className={`px-1.5 py-0.5 rounded-md text-xs font-black uppercase shrink-0 ${typeColor.badge}`}>{typeColor.label}</span>
                                                    {item.isExpired && <span className="px-1.5 py-0.5 rounded-md text-xs font-black uppercase shrink-0 bg-amber-50 text-amber-600 border border-amber-100">Quá hạn</span>}
                                                    <p className={`text-xs font-black truncate flex-1 min-w-0 ${isNewNotif ? 'text-slate-900' : 'text-slate-700'}`} title={item.title}>{item.title}</p>
                                    </div>
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <p className="text-xs text-slate-500 font-bold uppercase tracking-tight truncate flex-1 min-w-0">{item.subtitle} • {item.date ? new Date(item.date).toLocaleDateString('vi-VN') : 'Vừa xong'}</p>
                                                    {isGraded && item.submission && (
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            {item.submission.grade != null && (item.assignment as any)?.gradingSystem !== 'none' && (
                                                                <span className="px-2 py-0.5 bg-emerald-500 text-white rounded-lg text-xs font-black">
                                                                    {formatGrade(item.submission.grade, (item.assignment as any)?.gradingSystem)} điểm
                                                                </span>
                                                            )}
                                                            {item.submission.teacherComments && item.submission.teacherComments.trim() && (
                                                                <span className="text-xs text-slate-600 font-medium truncate max-w-[140px] italic" title={item.submission.teacherComments}>
                                                                    "{item.submission.teacherComments.trim().slice(0, 25)}{item.submission.teacherComments.trim().length > 25 ? '…' : ''}"
                                                                </span>
                                                            )}
                                                            {(!item.submission.grade || (item.assignment as any)?.gradingSystem === 'none') && !item.submission.teacherComments && item.statusLabel && (
                                                                <span className={`px-1.5 py-0.5 rounded-md text-xs font-black uppercase border shrink-0 ${item.statusColor}`}>{item.statusLabel}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {!isGraded && item.statusLabel && (
                                                        <span className={`px-1.5 py-0.5 rounded-md text-xs font-black uppercase border shrink-0 ${item.statusColor}`}>{item.statusLabel}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {canOpen && <span className="absolute top-2 right-2 text-[10px] font-black text-slate-400 bg-white/90 backdrop-blur-sm border border-slate-100 px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-all shadow-sm pointer-events-none">Chi tiết →</span>}
                            </button>
                        );
                    })
                    )}
                </div>
                    </section>

                    {/* Section: Classroom & Assignments */}
                    <section id="child-homework-section" className="space-y-8">
                        <div className="flex items-center justify-between px-2">
                            <h3 className="text-sm lg:text-base font-black text-slate-800 uppercase tracking-widest flex items-center gap-2.5">
                                <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center shadow-sm">
                                    <AcademicCapIcon className="h-3.5 w-3.5 text-blue-600" />
                                </div>
                                Lớp học & Bài tập của con
                            </h3>
                            <div className="flex gap-2">
                                <span className="px-3 py-1 bg-rose-50 text-rose-600 rounded-lg text-xs font-black uppercase border border-rose-100 shadow-sm">
                                    {incompleteItems.length} Chưa làm
                                </span>
                                {completedItems.length > 0 && (
                                    <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-black uppercase border border-emerald-100 shadow-sm">
                                        {completedItems.length} Xong
                                    </span>
                                )}
                            </div>
                        </div>

                        {childAssignments.length === 0 ? (
                            isLoading || resourceLoadingStates?.core || resourceLoadingStates?.teacher ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
                                    {[1, 2, 3].map(i => (
                                        <div key={i} className="h-64 bg-slate-50 rounded-[2.5rem] border border-slate-100 animate-pulse"></div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-16 bg-white rounded-[3rem] border border-slate-100 shadow-xl text-center">
                                    <div className="w-20 h-20 bg-slate-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 border border-slate-100 shadow-inner">
                                        <ClipboardDocumentListIcon className="h-10 w-10 text-slate-300" />
                                    </div>
                                    <h4 className="text-xl font-black text-slate-800 mb-3">Chưa có hoạt động nào</h4>
                                    <p className="text-slate-400 text-sm font-medium px-12 max-w-md mx-auto">Bé chưa nhận được bài tập hoặc thông báo nào từ các lớp học hiện tại.</p>
                                </div>
                            )
                        ) : (
                            <div className="space-y-10">
                                {/* Section 1: Incomplete Homework & Tasks */}
                                {incompleteItems.length > 0 && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2 flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                                            Nhiệm vụ & Bài tập cần làm
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 lg:gap-6">
                                            {incompleteItems.map(assignment => {
                                                const tc = getTypeColorConfig((assignment as any).type, false);
                                                const isTask = (assignment as any).type === 'task';
                                                const subType = (assignment as any).submissionType;
                                                const instructions = (assignment as any).instructions;
                                                
                                                return (
                                                    <div key={assignment.id} className={`p-5 lg:p-6 rounded-[2rem] lg:rounded-[2.5rem] border-2 transition-all group ${tc.bg} ${tc.border} hover:shadow-2xl hover:-translate-y-1 bg-white relative overflow-hidden flex flex-col h-full`}>
                                                        <div className={`absolute top-0 right-0 w-20 h-20 lg:w-24 lg:h-24 -mr-8 -mt-8 ${tc.badge} opacity-10 rounded-full group-hover:scale-110 transition-transform`} />
                                                        
                                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                                            <div className={`w-10 h-10 lg:w-11 lg:h-11 rounded-xl lg:rounded-2xl flex items-center justify-center ${tc.badge} shadow-md group-hover:scale-110 transition-transform`}>
                                                                {isTask ? <ClipboardDocumentListIcon className="h-5 w-5 lg:h-5.5 lg:w-5.5" /> : <PlayIcon className="h-5 w-5 lg:h-5.5 lg:w-5.5" />}
                                                            </div>
                                                            <div className="flex flex-col items-end gap-1">
                                                                <span className="px-2.5 py-1 bg-rose-500 text-white rounded-lg lg:rounded-xl text-xs lg:text-xs font-black uppercase tracking-wider animate-pulse shadow-lg shadow-rose-500/20">Cần làm</span>
                                                                {isTask && subType && (
                                                                    <span className="text-xs font-black text-amber-600 uppercase tracking-tighter bg-amber-50 px-1.5 py-0.5 rounded-md border border-amber-100">
                                                                        {subType === 'none' ? 'Chỉ xem' : subType === 'check_only' ? 'Xác nhận' : subType === 'photo' ? 'Nộp ảnh' : subType === 'audio_video' ? 'Video/Âm thanh' : 'Nộp bài'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="relative z-10 flex flex-col flex-1">
                                                            <h4 className="text-sm lg:text-base font-black text-slate-800 leading-tight group-hover:text-teal-600 transition-colors mb-2 line-clamp-2 min-h-[2.5rem] lg:min-h-[3rem]">{assignment.title}</h4>
                                                            
                                                            {instructions && (
                                                                <p className="text-xs text-slate-500 line-clamp-2 mb-3 font-medium leading-relaxed italic border-l-2 border-slate-100 pl-2">
                                                                    {instructions}
                                                                </p>
                                                            )}

                                                            <p className="text-xs lg:text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-1.5 text-left">
                                                                <AcademicCapIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" /> {assignment.className}
                                                            </p>
                                                            
                                                            <div className="flex flex-wrap gap-2 lg:gap-2.5 mb-6">
                                                                <span className={`px-1.5 py-0.5 lg:px-2 lg:py-1 rounded-md lg:rounded-lg text-xs lg:text-xs font-black uppercase border border-current opacity-70 ${tc.text}`}>{tc.label}</span>
                                                                <span className="px-1.5 py-0.5 lg:px-2 lg:py-1 bg-slate-50 text-slate-500 rounded-md lg:rounded-lg text-xs lg:text-xs font-black uppercase flex items-center gap-1 border border-slate-100">
                                                                    <ClockIcon className="h-2.5 w-2.5 lg:h-3 lg:w-3" /> Hạn: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}
                                                                </span>
                                                                {(() => {
                                                                    const dueMs = assignment.dueDate && String(assignment.dueDate).trim() ? new Date(assignment.dueDate).getTime() : 0;
                                                                    return dueMs > 0 && dueMs < Date.now() ? <span className="px-1.5 py-0.5 lg:px-2 lg:py-1 bg-amber-50 text-amber-600 rounded-md lg:rounded-lg text-xs font-black uppercase border border-amber-100">Quá hạn</span> : null;
                                                                })()}
                                                            </div>

                                                            <div className="flex gap-2 mt-auto">
                                                                <button 
                                                                    onClick={() => {
                                                                        const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                                                                        onNavigate(View.AssignmentDetail, { 
                                                                            assignmentId: assignment.id, 
                                                                            title: assignment.title, 
                                                                            classCode: cls?.inviteCode, 
                                                                            studentId: currentStudentId,
                                                                            isWorkingMode: currentIsWorkingMode
                                                                        });
                                                                    }}
                                                                    className={`flex-1 py-2.5 lg:py-3.5 rounded-xl lg:rounded-2xl text-xs lg:text-xs font-black uppercase tracking-[0.1em] transition-all flex items-center justify-center gap-2 shadow-xl ${isViewOnly ? 'bg-slate-800 text-white hover:bg-slate-900' : 'bg-teal-600 text-white hover:bg-teal-700 shadow-teal-500/20 active:scale-95'}`}
                                                                >
                                                                    {isViewOnly ? (
                                                                        <>
                                                                            <ZoomInIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
                                                                            {assignment.type === 'announcement' ? 'Xem thông báo' : 'Xem chi tiết'}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <PlayIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
                                                                            {assignment.type === 'announcement' ? 'Xem thông báo' : (assignment.type === 'homework' ? 'Làm bài tập' : 'Làm nhiệm vụ')}
                                                                        </>
                                                                    )}
                                                                </button>
                                                                
                                                                {isViewOnly && (
                                                                    <button 
                                                                        onClick={() => {
                                                                            const sp = studentProfile;
                                                                            if (onAddNotification && sp) {
                                                                                onAddNotification({
                                                                                    type: 'info',
                                                                                    message: `Phụ huynh nhắc con hoàn thành bài tập: "${assignment.title}"`,
                                                                                    icon: 'homework',
                                                                                    studentId: String(sp.id)
                                                                                }, true);
                                                                                alert(`Đã gửi lời nhắc hoàn thành bài tập "${assignment.title}" đến ${child.name}!`);
                                                                            } else {
                                                                                alert("Không tìm thấy thông tin học sinh để gửi thông báo.");
                                                                            }
                                                                        }}
                                                                        className="px-3 py-2.5 lg:px-4 lg:py-3.5 bg-amber-50 text-amber-600 rounded-xl lg:rounded-2xl border border-amber-100 hover:bg-amber-100 transition-all shadow-sm"
                                                                        title="Nhắc nhở con làm bài"
                                                                    >
                                                                        <BellIcon className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Section 2: Announcements */}
                                {childAnnouncements.length > 0 && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2 flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                            Thông báo & Tài liệu mới
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 lg:gap-6">
                                            {childAnnouncements.map(assignment => {
                                                const tc = getTypeColorConfig('announcement', false);
                                                const instructions = (assignment as any).instructions;

                                                return (
                                                    <div key={assignment.id} className="p-5 lg:p-6 rounded-[2rem] bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all group flex flex-col h-full relative overflow-hidden">
                                                        <div className={`absolute top-0 right-0 w-16 h-16 -mr-6 -mt-6 bg-violet-500 opacity-5 rounded-full group-hover:scale-110 transition-transform`} />
                                                        
                                                        <div className="flex justify-between items-start mb-4 relative z-10">
                                                            <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-xl lg:rounded-2xl flex items-center justify-center ${tc.badge} shadow-sm group-hover:scale-110 transition-transform`}>
                                                                <BellIcon className="h-4.5 w-4.5 lg:h-5 lg:w-5" />
                                                            </div>
                                                            <span className="px-2.5 py-1 bg-violet-500 text-white rounded-lg lg:rounded-xl text-xs lg:text-xs font-black uppercase tracking-wider shadow-sm">Thông báo</span>
                                                        </div>

                                                        <div className="relative z-10 flex flex-col flex-1">
                                                            <h4 className="text-sm lg:text-base font-black text-slate-800 leading-tight mb-2 group-hover:text-violet-600 transition-colors line-clamp-2 min-h-[2.5rem] lg:min-h-[3rem]">{assignment.title}</h4>
                                                            
                                                            {instructions && (
                                                                <p className="text-xs text-slate-500 line-clamp-2 mb-4 font-medium leading-relaxed italic border-l-2 border-slate-100 pl-2">
                                                                    {instructions}
                                                                </p>
                                                            )}

                                                            <p className="text-xs lg:text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-1.5 mt-auto">
                                                                <AcademicCapIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" /> {assignment.className}
                                                            </p>

                                                            <button 
                                                                onClick={() => {
                                                                    const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                                                                    onNavigate(View.AssignmentDetail, { assignmentId: assignment.id, title: assignment.title, classCode: cls?.inviteCode, studentId: currentStudentId });
                                                                }}
                                                                className="w-full py-2.5 lg:py-3 rounded-xl bg-slate-50 text-slate-600 font-black text-xs lg:text-xs uppercase tracking-[0.1em] hover:bg-violet-50 hover:text-violet-600 transition-all border border-slate-100 shadow-sm"
                                                            >
                                                                Xem thông báo
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Section 3: Completed / Results */}
                                {completedItems.length > 0 && (
                                    <div className="space-y-4">
                                        <h4 className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-[0.2em] px-2 flex items-center gap-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                            Bài đã nộp / Kết quả
                                        </h4>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 lg:gap-6">
                                            {completedItems.map(assignment => {
                                                const tc = getTypeColorConfig((assignment as any).type, isSubmissionGraded(assignment.status));
                                                const submission = submissions.find(s => String(s.assignmentId) === String(assignment.id) && (currentStudentId ? String(s.studentId) === String(currentStudentId) : false));
                                                const grade = submission?.grade;
                                                const isTask = (assignment as any).type === 'task';
                                                const subType = (assignment as any).submissionType;

                                                return (
                                                    <div key={assignment.id} className={`p-5 lg:p-6 rounded-[2rem] lg:rounded-[2.5rem] bg-white border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all group relative overflow-hidden flex flex-col h-full`}>
                                                        <div className={`absolute top-0 right-0 w-20 h-20 -mr-8 -mt-8 ${tc.badge} opacity-10 rounded-full group-hover:scale-110 transition-transform`} />
                                                        
                                                        <div className="flex justify-between items-start mb-4 lg:mb-6">
                                                            <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-xl lg:rounded-2xl flex items-center justify-center ${tc.badge} shadow-md group-hover:scale-110 transition-transform`}>
                                                                {grade !== undefined && grade !== null ? <TrophyIcon className="h-5 w-5 lg:h-5.5 lg:h-5.5" /> : <CheckBadgeIcon className="h-5 w-5 lg:h-5.5 lg:h-5.5" />}
                                                            </div>
                                                            <div className="flex flex-col items-end gap-1">
                                                                <span className={`px-2.5 py-1 ${grade !== undefined && grade !== null ? 'bg-emerald-500' : 'bg-blue-500'} text-white rounded-lg lg:rounded-xl text-xs lg:text-xs font-black uppercase tracking-wider shadow-sm`}>
                                                                    {grade !== undefined && grade !== null 
                                                                        ? (assignment.gradingSystem === 'none' ? 'Hoàn thành' : `${grade}/10`) 
                                                                        : (isSubmissionGraded(assignment.status) 
                                                                            ? (assignment.gradingSystem === 'none' ? 'Đã nhận xét' : 'Đã chấm') 
                                                                            : 'Đã nộp')}
                                                                </span>
                                                                {isTask && subType && (
                                                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter">
                                                                        {subType === 'none' ? 'Chỉ xem' : subType === 'check_only' ? 'Xác nhận' : subType === 'photo' ? 'Nộp ảnh' : subType === 'audio_video' ? 'Video/Âm thanh' : 'Nộp bài'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="relative z-10 flex flex-col flex-1">
                                                            <h4 className="text-sm lg:text-base font-black text-slate-800 leading-snug mb-2 group-hover:text-emerald-600 transition-colors line-clamp-2 h-[2.5rem] lg:h-[3rem]">{assignment.title}</h4>
                                                            
                                                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-1.5">
                                                                <AcademicCapIcon className="h-3 w-3" /> {assignment.className}
                                                            </p>

                                                            <div className="flex items-center gap-1.5 mb-6 text-xs lg:text-xs font-bold text-slate-400 mt-auto flex-wrap">
                                                                <div className={`w-1 h-1 rounded-full shrink-0 ${grade !== undefined && grade !== null ? 'bg-emerald-400' : 'bg-blue-400'}`} />
                                                                <span className="truncate">
                                                                    {isSubmissionGraded(assignment.status) 
                                                                        ? (assignment.gradingSystem === 'none' ? 'Đã xem & nhận xét' : 'Hoàn thành') 
                                                                        : 'Chờ chấm'}
                                                                </span>
                                                                {(() => {
                                                                    const dueMs = assignment.dueDate && String(assignment.dueDate).trim() ? new Date(assignment.dueDate).getTime() : 0;
                                                                    return dueMs > 0 && dueMs < Date.now() ? <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[10px] font-black uppercase border border-amber-100">Quá hạn</span> : null;
                                                                })()}
                                                                <span className="text-slate-300 ml-auto">{new Date(assignment.dueDate).toLocaleDateString('vi-VN')}</span>
                                                            </div>

                                                            <button 
                                                                onClick={() => {
                                                                    const cls = classes.find(c => String(c.id) === String(assignment.classId || (assignment as any).class_id));
                                                                    onNavigate(View.AssignmentDetail, { assignmentId: assignment.id, title: assignment.title, classCode: cls?.inviteCode, studentId: currentStudentId });
                                                                }}
                                                                className={`w-full py-2.5 lg:py-3 rounded-xl lg:rounded-2xl ${tc.badge} font-black text-xs lg:text-xs uppercase tracking-widest hover:brightness-105 active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg shadow-current/10`}
                                                            >
                                                                <ArrowPathIcon className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
                                                                {grade !== undefined && grade !== null 
                                                                    ? (assignment.gradingSystem === 'none' ? 'Xem nhận xét' : 'Kết quả') 
                                                                    : 'Xem lại'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {/* Section: Class Sessions & Teacher Comments */}
                    {childResultsByClass.some(r => r.sessionDetails.length > 0) && (
                        <section id="child-sessions-section" className="space-y-4">
                            <div className="flex items-center justify-between px-2">
                                <h3 className="text-lg font-black text-slate-800 uppercase tracking-widest flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
                                        <ChatBubbleBottomCenterTextIcon className="h-4 w-4 text-amber-600" />
                    </div>
                                    Lịch sử buổi học & Nhận xét
                                </h3>
                </div>

                            <div className="space-y-4">
                                {childResultsByClass.filter(r => r.sessionDetails.length > 0).map(result => (
                                    <div key={result.class.id} className="bg-white rounded-[2rem] border border-slate-100 overflow-hidden shadow-sm">
                                        <div className="bg-slate-50 px-6 py-3 border-b border-slate-100 flex justify-between items-center">
                                            <span className="text-xs font-black text-slate-500 uppercase tracking-wider">{result.class.name}</span>
                                            <span className="text-xs font-bold text-slate-400">{result.sessionDetails.length} buổi học</span>
                        </div>
                                        <div className="divide-y divide-slate-50">
                                            {result.sessionDetails.map((session: any) => (
                                                <div key={session.id} className="p-6 hover:bg-slate-50/50 transition-colors">
                                                    <div className="flex justify-between items-start mb-3">
                                                        <div>
                                                            <h4 className="font-black text-slate-800 text-sm">{session.title}</h4>
                                                            <p className="text-xs text-slate-400 font-medium uppercase mt-0.5">{new Date(session.date).toLocaleDateString('vi-VN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            {session.attendance === 'present' ? (
                                                                <span className="px-2 py-1 bg-emerald-50 text-emerald-600 text-xs font-black uppercase rounded-lg border border-emerald-100">Có mặt</span>
                                                            ) : session.attendance === 'absent' ? (
                                                                <span className="px-2 py-1 bg-rose-50 text-rose-600 text-xs font-black uppercase rounded-lg border border-rose-100">Vắng mặt</span>
                                                            ) : null}
                                                            {session.grade != null && (
                                                                <div className="text-right">
                                                                    <p className="text-xs font-black text-amber-500 uppercase tracking-widest leading-none mb-1">Điểm số</p>
                                                                    <p className="text-lg font-black text-amber-600 leading-none">{session.grade}/10</p>
                        </div>
                        )}
                    </div>
                                        </div>
                                                    {session.comments && (
                                                        <div className="bg-slate-50 rounded-2xl p-4 border border-slate-100/50 relative">
                                                            <div className="absolute -top-2 left-4 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-slate-50"></div>
                                                            <p className="text-xs text-slate-600 italic leading-relaxed font-medium">
                                                                <span className="font-black text-slate-400 mr-1 opacity-50">“</span>
                                                                {session.comments}
                                                                <span className="font-black text-slate-400 ml-1 opacity-50">”</span>
                                                            </p>
                                        </div>
                                        )}
                                    </div>
                                            ))}
                                </div>
                            </div>
                                ))}
                    </div>
                        </section>
                    )}

                    {/* Section: Learning Plans */}
                    <section className="space-y-4">
                        <div className="flex justify-between items-center px-2">
                            <h3 className="text-lg font-black text-slate-800 uppercase tracking-widest flex items-center gap-3">
                                <div className="w-8 h-8 rounded-xl bg-purple-100 flex items-center justify-center">
                                    <SparklesIcon className="h-4 w-4 text-purple-600" />
                                </div>
                                Lộ trình học tập cá nhân
                            </h3>
                            <div className="flex gap-3">
                                <button onClick={() => setIsHistoryModalOpen(true)} className="p-2 text-slate-400 hover:text-teal-600 transition-colors">
                                    <HistoryIcon className="h-5 w-5" />
                    </button>
                                {!isViewOnly && (
                    <button 
                        onClick={() => {
                            if (!checkLimit(learningPlans.length, limits.maxLearningPlans)) {
                                                alert(`Đã đạt giới hạn ${limits.maxLearningPlans} lộ trình.`);
                                onNavigate(View.CreditStore);
                                return;
                            }
                            setIsPlanModalOpen(true);
                        }} 
                                        className="bg-teal-600 text-white px-4 py-2 rounded-xl text-xs font-black shadow-lg shadow-teal-500/20 hover:bg-teal-700 transition-all active:scale-95 flex items-center gap-2"
                    >
                                        <PlusIcon className="h-4 w-4" /> Tạo mới
                    </button>
                                )}
                </div>
            </div>

                {learningPlans.length === 0 ? (
                            isLoading || resourceLoadingStates?.learningPlans ? (
                                <div className="space-y-6">
                                    {[1, 2].map(i => (
                                        <div key={i} className="h-64 bg-slate-50 rounded-[2.5rem] border border-slate-100 animate-pulse"></div>
                                    ))}
                                </div>
                            ) : (
                                <div className="p-10 bg-slate-50 rounded-[2.5rem] border border-dashed border-slate-200 text-center">
                                    <p className="text-slate-400 font-bold">Chưa có lộ trình học tập nào được tạo cho con.</p>
                                </div>
                            )
                ) : (
                            <div className="grid grid-cols-1 gap-6">
                        {learningPlans.map(plan => {
                            const status = getPlanStatus(plan);
                            const info = statusInfo[status];
                            return (
                                        <div key={plan.id} id={`plan-${plan.id}`} className={`bg-white rounded-[2.5rem] p-6 border border-slate-100 shadow-sm transition-all hover:shadow-xl ${focusedPlanId === String(plan.id) ? 'ring-2 ring-teal-500' : ''}`}>
                                            <div className="flex justify-between items-start mb-6">
                                                <div className="min-w-0">
                                                    <h4 className="text-xl font-black text-slate-800 tracking-tight">{plan.title}</h4>
                                                    <p className="text-xs text-slate-400 mt-1 italic font-medium truncate flex items-center gap-2"><FlagIcon className="h-3 w-3" /> {plan.goal}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                                    <div className={`flex items-center gap-1.5 px-3 py-1 text-xs font-black rounded-xl uppercase tracking-widest ${colorClasses[info.color]}`}>
                                                        {info.icon} {info.text}
                                                    </div>
                                                    {!isViewOnly && (
                                                        <div className="flex items-center gap-1">
                                                            <button 
                                                                onClick={() => {
                                                                    const newTitle = prompt('Nhập tiêu đề mới cho lộ trình:', plan.title);
                                                                    if (newTitle !== null && newTitle.trim() !== '') {
                                                                        onUpdatePlan({ ...plan, title: newTitle.trim() });
                                                                    }
                                                                }}
                                                                className="p-2 rounded-full hover:bg-blue-50 transition-colors"
                                                                title="Sửa tên lộ trình"
                                                            >
                                                                <PencilIcon className="h-4 w-4 text-blue-400" />
                                                            </button>
                                                            <button onClick={() => { if(confirm(`Bạn có chắc muốn xóa kế hoạch "${plan.title}"?`)) onDeletePlan(plan.id) }} className="p-2 rounded-full hover:bg-red-50 transition-colors">
                                                                <TrashIcon className="h-4 w-4 text-red-400" />
                                                            </button>
                                                        </div>
                                                    )}
                                        </div>
                                    </div>
                                    
                                            <div className="grid grid-cols-3 gap-3 mb-6">
                                                <div className="bg-slate-50 p-3 rounded-2xl text-center border border-slate-100/50">
                                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Mức độ</p>
                                                    <p className="text-xs font-black text-slate-700">{plan.difficulty}</p>
                                                </div>
                                                <div className="bg-slate-50 p-3 rounded-2xl text-center border border-slate-100/50">
                                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Thời lượng</p>
                                                    <p className="text-xs font-black text-slate-700">{plan.duration}</p>
                                                </div>
                                                <div className="bg-slate-50 p-3 rounded-2xl text-center border border-slate-100/50">
                                                    <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-1">Bài học</p>
                                                    <p className="text-xs font-black text-slate-700">{plan.dailyPlans.flatMap(d => d.modules).length}</p>
                                                </div>
                                            </div>

                                            {/* Daily Plans Accordion */}
                                            <div className="space-y-3">
                                                {plan.dailyPlans.map((dayPlan, index) => (
                                                    <details key={index} className="group" open={index === 0 || dayPlan.modules.some(m => String(m.id) === focusedModuleId)}>
                                                        <summary className="flex justify-between items-center p-4 bg-slate-50 rounded-2xl cursor-pointer hover:bg-slate-100 transition-colors border border-slate-100/50">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center font-black text-xs text-slate-800 border border-slate-100 shadow-sm">
                                                                    {index + 1}
                                                                </div>
                                                                <h5 className="font-black text-sm text-slate-800">{dayPlan.day}</h5>
                                                            </div>
                                                            <ChevronDownIcon className="h-5 w-5 text-slate-400 transition-transform group-open:rotate-180" />
                                                    </summary>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 p-4">
                                                        {dayPlan.modules.map((module, moduleIndex) => (
                                                             <div 
                                                                key={module.id || `mod-${index}-${moduleIndex}`} 
                                                                id={`module-${module.id}`}
                                                                    className={`p-4 rounded-3xl flex flex-col justify-between border-2 transition-all ${module.status === 'completed' ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-slate-100 hover:shadow-lg'} ${String(module.id) === focusedModuleId ? 'ring-2 ring-teal-500 shadow-lg' : ''}`}
                                                            >
                                                                <div>
                                                                        <div className="flex justify-between items-start mb-3">
                                                                            <p className="font-black text-sm text-slate-800 leading-tight flex-1">{module.title}</p>
                                                                        <div className="flex items-center gap-1">
                                                                                {!isViewOnly && (
                                                                                    <>
                                                                            <button 
                                                                                onClick={() => setEditingModule({ planId: plan.id, dayIndex: index, moduleIndex, module })}
                                                                                            className="p-1 text-slate-300 hover:text-blue-500 transition-colors"
                                                                            >
                                                                                <PencilIcon className="h-3.5 w-3.5" />
                                                                            </button>
                                                                            <button 
                                                                                onClick={() => handleDeleteModule(plan.id, index, moduleIndex)}
                                                                                            className="p-1 text-slate-300 hover:text-red-500 transition-colors"
                                                                            >
                                                                                <TrashIcon className="h-3.5 w-3.5" />
                                                                            </button>
                                                                                    </>
                                                                                )}
                                                                                {module.status === 'completed' && <CheckBadgeIcon className="h-5 w-5 text-emerald-500 shrink-0" />}
                                                                        </div>
                                                                    </div>
                                                                    {module.imageUrl && (
                                                                            <div className="mb-3 rounded-2xl overflow-hidden aspect-video bg-slate-50 border border-slate-100">
                                                                            <img src={formatAssetUrl(module.imageUrl)} alt={module.title} className="w-full h-full object-cover" />
                                                                        </div>
                                                                    )}
                                                                        <p className="text-xs text-slate-500 line-clamp-2 font-medium leading-relaxed mb-4">{module.content}</p>
                                                                </div>
                                                                    <button 
                                                                        onClick={() => onNavigate(View.LearningModuleView, { moduleId: module.id, planId: plan.id, module, plan, isPreview: !isWorkingMode })}
                                                                        className={`w-full py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${module.status === 'completed' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-teal-600 text-white hover:bg-teal-700 shadow-lg shadow-teal-500/10'}`}
                                                                    >
                                                                        {module.status === 'completed' ? 'Ôn lại bài' : 'Bắt đầu học'}
                                                                    </button>
                                                             </div>
                                                        ))}
                                                            {!isViewOnly && (
                                                        <button 
                                                            onClick={() => setEditingModule({ planId: plan.id, dayIndex: index, moduleIndex: -1, module: { ...EMPTY_MODULE } })}
                                                                    className="p-4 border-2 border-dashed border-slate-200 rounded-3xl flex flex-col items-center justify-center gap-2 text-slate-300 hover:border-teal-500 hover:text-teal-500 transition-all bg-slate-50/50 min-h-[140px] group"
                                                        >
                                                                    <PlusIcon className="h-8 w-8 group-hover:scale-110 transition-transform" />
                                                                    <span className="text-xs font-black uppercase tracking-widest">Thêm bài học</span>
                                                        </button>
                                                            )}
                                                    </div>
                                                </details>
                                                ))}
                                    </div>
                                        </div>
                                    );
                        })}
                    </div>
                )}
                    </section>
                </div>

                {/* Right Column (Sidebar) */}
                <div className="lg:col-span-4 xl:col-span-3 space-y-6 lg:space-y-8">
                    {/* Section: Learning History Stats */}
                    {isLoading || resourceLoadingStates?.core || resourceLoadingStates?.learningPlans ? (
                        <div className="h-64 bg-slate-100 rounded-[2.5rem] border border-slate-200 animate-pulse"></div>
                    ) : (
                        <section className="bg-gradient-to-br from-indigo-600 to-blue-700 rounded-[2rem] lg:rounded-[2.5rem] p-6 text-white shadow-xl relative overflow-hidden group">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl -mr-10 -mt-10 group-hover:scale-110 transition-transform"></div>
                            <h3 className="text-xs lg:text-xs font-black uppercase tracking-widest mb-6 flex items-center gap-2 relative z-10">
                                <HistoryIcon className="h-4 w-4" /> Hoạt động tự học
                            </h3>
                            <div className="space-y-5 relative z-10">
                                <div className="flex items-center gap-4">
                                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0 border border-white/20 backdrop-blur-md">
                                        <SparklesIcon className="h-4.5 w-4.5" />
                                    </div>
                                    <div>
                                        <p className="text-xl lg:text-2xl font-black leading-none">{learningHistory.length}</p>
                                        <p className="text-xs font-bold text-indigo-200 uppercase tracking-widest mt-1">Nội dung hoàn thành</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center shrink-0 border border-white/20 backdrop-blur-md">
                                        <ClockIcon className="h-4.5 w-4.5" />
                                    </div>
                                    <div>
                                        <p className="text-xl lg:text-2xl font-black leading-none">{learningPlans.length}</p>
                                        <p className="text-xs font-bold text-indigo-200 uppercase tracking-widest mt-1">Lộ trình thực hiện</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setIsHistoryModalOpen(true)}
                                    className="w-full py-2.5 bg-white text-indigo-700 rounded-xl lg:rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-indigo-50 transition-all active:scale-95 shadow-lg mt-2"
                                >
                                    Xem lịch sử
                                </button>
                            </div>
                        </section>
                    )}

                    {/* Section: Quick Actions / AI Assistant */}
                    <section className="bg-white p-6 rounded-[2rem] lg:rounded-[2.5rem] border border-slate-100 shadow-sm space-y-4">
                        <h3 className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-widest px-2">Hỗ trợ học tập</h3>
                        <button 
                            className="w-full p-3.5 bg-teal-50 border border-teal-100 rounded-[1.8rem] flex items-center gap-3.5 group hover:bg-teal-100 transition-all text-left"
                            onClick={() => onNavigate(View.Classes)}
                        >
                            <div className="w-9 h-9 rounded-xl bg-teal-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                                <PlusIcon className="h-4.5 w-4.5" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-black text-teal-800 uppercase tracking-tight truncate">Tìm lớp học mới</p>
                                <p className="text-xs text-teal-600 font-medium truncate">Tham gia lớp của thầy cô</p>
                            </div>
                        </button>
                        <button 
                            className="w-full p-3.5 bg-purple-50 border border-purple-100 rounded-[1.8rem] flex items-center gap-3.5 group hover:bg-purple-100 transition-all text-left"
                            onClick={() => setIsPlanModalOpen(true)}
                        >
                            <div className="w-9 h-9 rounded-xl bg-purple-600 text-white flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                                <SparklesIcon className="h-4.5 w-4.5" />
                            </div>
                            <div className="min-w-0">
                                <p className="text-xs font-black text-purple-800 uppercase tracking-tight truncate">Tạo bài học AI</p>
                                <p className="text-xs text-purple-600 font-medium truncate">Học bất cứ điều gì con muốn</p>
                            </div>
                        </button>
                    </section>

                    {/* Section: Result Summary per Class */}
                    {childResultsByClass.length > 0 ? (
                        <section className="space-y-4">
                            <h3 className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-widest px-2">Kết quả theo lớp học</h3>
                            <div className="grid grid-cols-1 gap-4">
                                {childResultsByClass.map(({ class: cls, completed, totalAssignments, avgGrade, attendance }) => (
                                    <div key={cls.id} className="bg-white p-4 lg:p-5 rounded-[2rem] lg:rounded-[2.5rem] border border-slate-100 shadow-sm hover:shadow-xl transition-all group">
                                        <div className="flex items-center gap-3 mb-4 lg:mb-5">
                                            <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0 text-indigo-600 group-hover:scale-110 transition-transform shadow-sm">
                                                <AcademicCapIcon className="h-4.5 w-4.5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-black text-slate-800 text-xs lg:text-sm truncate">{cls.name}</h4>
                                                <p className="text-xs lg:text-xs text-slate-400 font-bold uppercase tracking-widest truncate">GV: {cls.teacherName || 'Chưa cập nhật'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 lg:gap-3 mb-4">
                                            <div className="p-2.5 bg-slate-50 rounded-2xl text-center border border-slate-100/50">
                                                <p className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-widest mb-1 leading-none">Điểm TB</p>
                                                <p className="text-sm lg:text-base font-black text-indigo-600 leading-none">{avgGrade || '—'}</p>
                                            </div>
                                            <div className="p-2.5 bg-slate-50 rounded-2xl text-center border border-slate-100/50">
                                                <p className="text-xs lg:text-xs font-black text-slate-400 uppercase tracking-widest mb-1 leading-none">Chuyên cần</p>
                                                <p className="text-sm lg:text-base font-black text-emerald-600 leading-none">{attendance ? `${attendance}%` : '—'}</p>
                                            </div>
                                        </div>
                                        <div className="space-y-1.5">
                                            <div className="flex justify-between text-xs lg:text-xs font-black text-slate-500 uppercase tracking-widest mb-0.5 px-1">
                                                <span>Tiến độ bài tập</span>
                                                <span>{completed}/{totalAssignments}</span>
                                            </div>
                                            <div className="h-1 lg:h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                <div 
                                                    className="h-full bg-gradient-to-r from-teal-400 to-blue-500 transition-all duration-1000"
                                                    style={{ width: `${totalAssignments > 0 ? (completed / totalAssignments) * 100 : 0}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ) : (
                        (isLoading || resourceLoadingStates?.core) && (
                            <div className="h-48 bg-slate-50 rounded-[2rem] border border-slate-100 animate-pulse"></div>
                        )
                    )}
                </div>
            </div>
            
            {/* Mode Switcher for Parent */}
            {userSettings?.role === 'parent' && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[110] flex justify-center w-full max-w-xs px-4 pointer-events-none">
                    <button 
                        onClick={handleSwitchMode}
                        className={`w-full h-12 flex items-center justify-center gap-2.5 font-black text-xs uppercase tracking-[0.15em] rounded-full shadow-2xl transition-all pointer-events-auto hover:-translate-y-1 active:scale-95 ${
                            currentIsWorkingMode 
                            ? 'bg-slate-800/90 backdrop-blur-xl text-white border border-white/10 hover:bg-slate-900' 
                            : 'bg-teal-600 text-white shadow-teal-500/20 hover:bg-teal-700'
                        }`}
                    >
                        {currentIsWorkingMode ? (
                            <>
                                <XMarkIcon className="h-4 w-4 text-rose-400" />
                                Thoát chế độ con học
                            </>
                        ) : (
                            <>
                                <PlayIcon className="h-4 w-4 text-teal-300" />
                                Mở chế độ cho con học
                            </>
                        )}
                    </button>
                </div>
            )}
            </div>
    );
};

export default ChildDashboard;
